import { useState } from 'react';
import {
  BookOpen,
  Database,
  Shield,
  GitBranch,
  AlertTriangle,
  Download,
  Server,
  Key,
  Code2,
  Loader2,
  Link2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  GraduationCap,
  Lightbulb,
  Info,
  CheckCircle2,
  Sparkles,
  Users,
  FileText,
  ClipboardList,
  Award,
  Globe,
  Calendar,
  MessageSquare,
  PlayCircle,
  Mail
} from 'lucide-react';
import { INTEGRATION_GUIDES, LmsType } from '../../data/integrationGuides';
import { getUser } from '@/lib/auth';
import jsPDF from 'jspdf';

type Section =
  | 'user-guide'
  | 'overview'
  | 'architecture'
  | 'api'
  | 'database'
  | 'deployment'
  | 'security'
  | 'troubleshooting'
  | 'code-examples'
  | 'integrations-overview'
  | 'moodle'
  | 'tutorlms'
  | 'learndash'
  | 'xtramile';

// Hvilke seksjoner som er synlige per rolle.
// member = ordinære kursleverandører — pedagogisk veiledning + integrasjoner
// admin (forbund) + systemadmin = full teknisk dokumentasjon
const SECTIONS_BY_ROLE: Record<string, Section[]> = {
  member: ['user-guide', 'integrations-overview', 'moodle', 'tutorlms', 'learndash', 'xtramile'],
  sensor: ['user-guide'],
  admin: ['user-guide', 'overview', 'architecture', 'api', 'database', 'code-examples', 'deployment', 'security', 'troubleshooting', 'integrations-overview', 'moodle', 'tutorlms', 'learndash', 'xtramile'],
  systemadmin: ['user-guide', 'overview', 'architecture', 'api', 'database', 'code-examples', 'deployment', 'security', 'troubleshooting', 'integrations-overview', 'moodle', 'tutorlms', 'learndash', 'xtramile']
};

export default function Documentation() {
  const currentUser = getUser();
  const role = currentUser?.role || 'member';
  const visibleSections = SECTIONS_BY_ROLE[role] || SECTIONS_BY_ROLE.member;
  const canSee = (s: Section) => visibleSections.includes(s);

  const [activeSection, setActiveSection] = useState<Section>(visibleSections[0] || 'overview');
  const [integrationsExpanded, setIntegrationsExpanded] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number = 10) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper function to add wrapped text
      const addText = (text: string, fontSize: number, isBold: boolean = false, color: number[] = [0, 0, 0]) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.setTextColor(color[0], color[1], color[2]);

        const lines = pdf.splitTextToSize(text, contentWidth);
        const lineHeight = fontSize * 0.5;

        lines.forEach((line: string) => {
          checkNewPage(lineHeight);
          pdf.text(line, margin, yPos);
          yPos += lineHeight;
        });
      };

      // Title Page
      pdf.setFillColor(37, 99, 235); // KF blue
      pdf.rect(0, 0, pageWidth, 50, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('API-dokumentasjon', pageWidth / 2, 25, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Kompetanseattest Kvalitetssikring System', pageWidth / 2, 35, { align: 'center' });

      yPos = 65;

      // System Information
      addText('System: Kompetanseattest Kvalitetssikring', 14, true, [37, 99, 235]);
      yPos += 3;
      addText(`Generert: ${new Date().toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' })}`, 10, false, [100, 100, 100]);
      yPos += 8;

      // Base URL Section
      addText('Base URL', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no', 10);
      yPos += 8;

      // Authentication Section
      addText('Autentisering', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('Alle API-kall (unntatt offentlig verifisering) krever Bearer token i Authorization header.', 10);
      yPos += 8;

      // Rate Limiting
      addText('Rate Limiting', 16, true, [37, 99, 235]);
      yPos += 3;
      addText('• Generelle endepunkter: 100 requests/minutt', 10);
      addText('• AI-endepunkter: 10 requests/minutt', 10);
      addText('• Autentisering: 5 forsøk/15 minutter', 10);
      yPos += 10;

      checkNewPage(20);

      // API Type Introduction
      addText('To typer API-tilgang', 18, true, [37, 99, 235]);
      yPos += 5;
      addText('Systemet tilbyr to separate API-er med forskjellige autentiseringsmetoder og tilgangsnivåer.', 10);
      yPos += 10;

      // =============================================================
      // EXTERNAL LMS API (API-LEGITIMASJON)
      // =============================================================
      checkNewPage(20);
      pdf.setFillColor(37, 99, 235);
      pdf.rect(margin - 5, yPos, contentWidth + 10, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('API-LEGITIMASJON (EKSTERNE LMS)', margin, yPos + 8);
      pdf.setTextColor(0, 0, 0);
      yPos += 18;

      addText('For eksterne LMS, partnere og kursleverandører. Studieplaner, versjoner, eksamen, webhooks.', 10);
      yPos += 8;

      addText('Base URL:', 12, true);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no/api/v1', 9);
      yPos += 8;

      addText('Interaktiv dokumentasjon:', 12, true);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no/api/v1/docs (Swagger UI)', 9);
      addText('https://studieplan.kompetanseattest.no/api/v1/openapi.json (OpenAPI 3.1 spec)', 9);
      yPos += 8;

      addText('Autentisering:', 12, true);
      yPos += 3;
      addText('X-API-Username: [ditt-api-brukernavn]', 9);
      addText('X-API-Key: [din-hemmelige-nøkkel]', 9);
      yPos += 8;

      addText('Tilgangsrammer (påtvunget basert på credential-eier):', 12, true);
      yPos += 3;
      addText('• Member-credential: ser kun studieplaner fra egen organisasjon', 9);
      addText('• Forbund-credential: ser studieplaner i hele studieforbundet', 9);
      addText('• Systemadmin-credential: full tilgang (kun for formelle partnere)', 9);
      yPos += 8;

      addText('Rate Limiting: 1000 requests/time (konfigurerbart per credential)', 10);
      yPos += 8;

      addText('STUDIEPLAN-ENDEPUNKTER:', 11, true);
      yPos += 5;
      addText('GET /api/v1/study-plans', 10, true);
      addText('  List studieplaner. Filter: ?eqf_level, ?subject_area, ?updated_since', 9);
      addText('  Versjoner: ?include_versions=latest|all', 9);
      addText('  Inkluder: ?include=literature,esco,bloom,assessment,modules', 9);
      addText('  Paginering: ?page, ?per_page (max 100)', 9);
      yPos += 5;
      addText('GET /api/v1/study-plans/:id', 10, true);
      addText('  Hent siste godkjente versjon. Format: ?format=json|edc|elm|html', 9);
      addText('  ETag/If-None-Match støttes for caching', 9);
      yPos += 5;
      addText('GET /api/v1/study-plans/:id/versions', 10, true);
      addText('  List alle godkjente versjoner', 9);
      yPos += 5;
      addText('GET /api/v1/study-plans/:id/versions/:version_no', 10, true);
      addText('  Hent spesifikk versjon med format-forhandling', 9);
      yPos += 5;
      addText('GET /api/v1/study-plans/by-link/:approval_link (uautentisert)', 10, true);
      addText('  Public verifikasjon — bruker QR-kode-lenke fra attester', 9);
      yPos += 8;

      addText('FORMATER (?format=...):', 11, true);
      yPos += 3;
      addText('• json (default): Strukturert JSON med valgfrie felt', 9);
      addText('• edc: W3C Verifiable Credential + EDC-AP JSON-LD', 9);
      addText('• elm: ELM v3 XML (CEN EN 15981) — for EU-kompatibilitet', 9);
      addText('• html: HTML-chunk med Schema.org Course microdata (for embedding/SEO)', 9);
      yPos += 8;

      addText('oEMBED:', 11, true);
      yPos += 3;
      addText('GET /api/v1/oembed?url=<lenke> — for WordPress og andre CMS-er', 9);
      yPos += 8;

      addText('WEBHOOKS:', 11, true);
      yPos += 3;
      addText('POST /api/v1/webhooks — registrer webhook (returnerer hmac_secret én gang)', 9);
      addText('GET /api/v1/webhooks — list dine webhooks', 9);
      addText('DELETE /api/v1/webhooks/:id — slett webhook', 9);
      addText('Events: study_plan.approved, study_plan.version_approved', 9);
      addText('Signering: X-Kompetanse-Signature: sha256=<hex> (HMAC av request body)', 9);
      yPos += 10;

      addText('EKSAMEN-ENDEPUNKTER (uendret fra v1.0):', 11, true);
      yPos += 5;

      // POST /api/v1/exam/start
      checkNewPage(30);
      addText('POST /api/v1/exam/start', 13, true, [37, 99, 235]);
      yPos += 3;
      addText('Start prøvesesjon for kandidat', 9);
      yPos += 5;
      pdf.setFillColor(250, 250, 250);
      const examStartHeight = 30;
      checkNewPage(examStartHeight);
      pdf.rect(margin, yPos, contentWidth, examStartHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('Request: { "course_link": "abc123", "candidate_code": "student-001",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "extended_time_minutes": 30, "metadata": {...} }', margin + 5, yPos);
      yPos += 6;
      pdf.text('Response: { "success": true, "session_id": "uuid",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "exam_url": "https://...", "expires_at": "..." }', margin + 5, yPos);
      yPos += examStartHeight - 15;
      yPos += 10;

      // GET /api/v1/exam/result/:session_id
      checkNewPage(30);
      addText('GET /api/v1/exam/result/:session_id', 13, true, [37, 99, 235]);
      yPos += 3;
      addText('Hent prøveresultat etter levering', 9);
      yPos += 5;
      pdf.setFillColor(250, 250, 250);
      const examResultHeight = 35;
      checkNewPage(examResultHeight);
      pdf.rect(margin, yPos, contentWidth, examResultHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('URL: /api/v1/exam/result/550e8400-e29b-41d4...', margin + 5, yPos);
      yPos += 5;
      pdf.text('Response (graded): { "success": true, "status": "graded",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "score": 85, "max_score": 100, "percentage": 85,', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "passed": true, "answers": [...] }', margin + 5, yPos);
      yPos += 6;
      pdf.text('Response (in_progress): { "success": true, "status": "in_progress",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "message": "Exam not yet submitted" }', margin + 5, yPos);
      yPos += examResultHeight - 23;
      yPos += 10;

      addText('HTTP Statuskoder:', 11, true);
      yPos += 4;
      addText('• 200: Suksess', 9);
      addText('• 400: Ugyldig forespørsel', 9);
      addText('• 401: Ugyldig API-legitimasjon', 9);
      addText('• 403: IP ikke hvitelistet / Org suspendert', 9);
      addText('• 404: Kurs/sesjon ikke funnet', 9);
      addText('• 409: Kandidat har allerede aktiv sesjon', 9);
      addText('• 429: Rate limit overskredet', 9);
      addText('• 500: Serverfeil', 9);
      yPos += 12;

      // =============================================================
      // INTERNAL API (BRUKERAUTENTISERING)
      // =============================================================
      checkNewPage(20);
      pdf.setFillColor(147, 51, 234); // Purple
      pdf.rect(margin - 5, yPos, contentWidth + 10, 12, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INTERN API (BRUKERAUTENTISERING)', margin, yPos + 8);
      pdf.setTextColor(0, 0, 0);
      yPos += 18;

      addText('For påloggede brukere. Full tilgang til alle funksjoner (36 endpoints).', 10);
      yPos += 8;

      addText('Base URL:', 12, true);
      yPos += 3;
      addText('https://studieplan.kompetanseattest.no', 9);
      yPos += 8;

      addText('Autentisering:', 12, true);
      yPos += 3;
      addText('Authorization: Bearer [jwt-token-fra-innlogging]', 9);
      yPos += 8;

      addText('Rate Limiting:', 12, true);
      yPos += 3;
      addText('• Generelle: 100 req/min', 9);
      addText('• AI-endepunkter: 10 req/min', 9);
      addText('• Autentisering: 5 forsøk/15 min', 9);
      yPos += 10;

      // API Categories Overview
      addText('API Kategorier (36 endpoints):', 14, true, [37, 99, 235]);
      yPos += 5;

      const categories = [
        { name: 'Autentisering', count: 5, desc: 'Login, registrering, e-postverifisering' },
        { name: 'Studieplaner', count: 7, desc: 'CRUD-operasjoner, validering, godkjenning' },
        { name: 'Litteratur', count: 6, desc: 'Hybrid AI litteraturforslag (v1.6)' },
        { name: 'Avsluttende prøver', count: 8, desc: 'Generering, gjennomføring, tilrettelegging' },
        { name: 'Administrasjon', count: 10, desc: 'Brukere, backup, kurerte kilder' }
      ];

      categories.forEach(cat => {
        checkNewPage(15);
        addText(`${cat.name} (${cat.count} endpoints)`, 12, true);
        yPos += 1;
        addText(cat.desc, 9, false, [100, 100, 100]);
        yPos += 6;
      });

      yPos += 5;

      // Detailed Endpoints - Authentication
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('AUTENTISERING (5 endpoints)', 14, true, [37, 99, 235]);
      yPos += 8;

      // POST /auth/login
      checkNewPage(25);
      addText('POST /auth/login', 12, true);
      yPos += 3;
      addText('Logg inn bruker med e-post og passord.', 9);
      yPos += 5;
      addText('Request:', 10, true);
      yPos += 2;
      pdf.setFillColor(250, 250, 250);
      const loginReqHeight = 15;
      checkNewPage(loginReqHeight);
      pdf.rect(margin, yPos, contentWidth, loginReqHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('{ "email": "bruker@example.com", "password": "passord123" }', margin + 5, yPos);
      yPos += loginReqHeight;

      addText('Response: 200 OK med { "success": true, "user": {...}, "token": "..." }', 9);
      yPos += 10;

      // POST /auth/register
      checkNewPage(25);
      addText('POST /auth/register', 12, true);
      yPos += 3;
      addText('Registrer ny bruker.', 9);
      yPos += 5;
      addText('Request:', 10, true);
      yPos += 2;
      const regReqHeight = 20;
      checkNewPage(regReqHeight);
      pdf.setFillColor(250, 250, 250);
      pdf.rect(margin, yPos, contentWidth, regReqHeight, 'F');
      yPos += 5;
      pdf.setFontSize(8);
      pdf.text('{ "email": "...", "password": "...", "full_name": "...",', margin + 5, yPos);
      yPos += 4;
      pdf.text('  "study_association_id": "uuid", "role": "member" }', margin + 5, yPos);
      yPos += regReqHeight - 4;
      yPos += 10;

      // Other auth endpoints (abbreviated)
      checkNewPage(15);
      addText('POST /auth/verify-email', 12, true);
      addText('Verifiser e-postadresse med kode.', 9);
      yPos += 8;

      checkNewPage(15);
      addText('POST /auth/forgot-password', 12, true);
      addText('Send e-post for passordtilbakestilling.', 9);
      yPos += 8;

      checkNewPage(15);
      addText('POST /auth/reset-password', 12, true);
      addText('Tilbakestill passord med token.', 9);
      yPos += 12;

      // Study Plans Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('STUDIEPLANER (7 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      const studyPlanEndpoints = [
        { method: 'POST', path: '/api/study-plans/list', desc: 'Hent liste over studieplaner' },
        { method: 'POST', path: '/api/study-plans/get', desc: 'Hent én studieplan' },
        { method: 'POST', path: '/api/study-plans/create', desc: 'Opprett ny studieplan' },
        { method: 'POST', path: '/api/study-plans/update', desc: 'Oppdater studieplan' },
        { method: 'POST', path: '/api/study-plans/validate', desc: 'Valider med AI (GPT-4)' },
        { method: 'POST', path: '/api/study-plans/approve', desc: 'Godkjenn studieplan (admin)' },
        { method: 'POST', path: '/api/study-plans/reject', desc: 'Avvis studieplan (admin)' }
      ];

      studyPlanEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Literature Section with Hybrid AI highlight
      checkNewPage(25);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('LITTERATUR (6 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      // Highlight box for v1.6 feature
      checkNewPage(20);
      pdf.setFillColor(219, 234, 254); // Light blue
      pdf.rect(margin, yPos, contentWidth, 18, 'F');
      yPos += 5;
      addText('✨ Hybrid AI Litteratursystem (v1.6)', 11, true, [37, 99, 235]);
      yPos += 4;
      addText('Kombinerer AI-genererte forslag med kurerte kilder for redusert hallusinasjon.', 8);
      yPos += 12;

      const literatureEndpoints = [
        { method: 'GET', path: '/api/study-plans/:id/literature', desc: 'Hent litteraturliste' },
        { method: 'POST', path: '/api/study-plans/:id/literature', desc: 'Legg til litteratur' },
        { method: 'POST', path: '/api/study-plans/:id/literature/ai-suggest', desc: 'KI-forslag (hybrid)' },
        { method: 'PUT', path: '/api/study-plans/:id/literature/:lit_id', desc: 'Oppdater litteratur' },
        { method: 'DELETE', path: '/api/study-plans/:id/literature/:lit_id', desc: 'Slett litteratur' },
        { method: 'POST', path: '/api/study-plans/:id/literature/:lit_id/download', desc: 'Last ned PDF' }
      ];

      literatureEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Exams Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('AVSLUTTENDE PRØVER (8 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      const examEndpoints = [
        { method: 'GET', path: '/api/exams/list', desc: 'Hent tilgjengelige prøver' },
        { method: 'POST', path: '/api/exams/generate', desc: 'Generer ny prøve' },
        { method: 'POST', path: '/api/v1/exam/start', desc: 'Start prøve (anonym sesjon)' },
        { method: 'POST', path: '/api/exam/save-answer', desc: 'Lagre svar' },
        { method: 'POST', path: '/api/exam/submit', desc: 'Lever prøve' },
        { method: 'GET', path: '/api/v1/exam/result/:session_id', desc: 'Hent resultat' },
        { method: 'GET', path: '/api/accommodations/templates', desc: 'Hent tilretteleggingsmaler' },
        { method: 'POST', path: '/api/accommodations/session/:session_id', desc: 'Aktiver tilrettelegging' }
      ];

      examEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Admin Section
      checkNewPage(20);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('ADMINISTRASJON (10 endpoints)', 14, true, [37, 99, 235]);
      yPos += 10;

      checkNewPage(15);
      pdf.setFillColor(254, 242, 242); // Light red
      pdf.rect(margin, yPos, contentWidth, 12, 'F');
      yPos += 4;
      addText('⚠️ Krever admin eller systemadmin rolle', 10, true, [220, 38, 38]);
      yPos += 10;

      const adminEndpoints = [
        { method: 'GET', path: '/api/admin/users', desc: 'Hent brukerliste' },
        { method: 'POST', path: '/api/admin/users/approve', desc: 'Godkjenn bruker' },
        { method: 'POST', path: '/api/admin/users/update', desc: 'Oppdater bruker' },
        { method: 'GET', path: '/api/admin/study-associations', desc: 'Hent studieforbund' },
        { method: 'GET', path: '/api/admin/backups', desc: 'List backups' },
        { method: 'POST', path: '/api/admin/backups/create', desc: 'Opprett backup' },
        { method: 'GET', path: '/api/admin/curated-sources', desc: 'Hent kurerte kilder (v1.6)' },
        { method: 'POST', path: '/api/admin/curated-sources', desc: 'Opprett kilde (v1.6)' },
        { method: 'PUT', path: '/api/admin/curated-sources/:id', desc: 'Oppdater kilde (v1.6)' },
        { method: 'DELETE', path: '/api/admin/curated-sources/:id', desc: 'Slett kilde (v1.6)' }
      ];

      adminEndpoints.forEach(endpoint => {
        checkNewPage(12);
        addText(`${endpoint.method} ${endpoint.path}`, 11, true);
        yPos += 2;
        addText(endpoint.desc, 9);
        yPos += 7;
      });

      // Error Handling Section
      checkNewPage(25);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos, contentWidth, 10, 'F');
      addText('FEILHÅNDTERING', 14, true, [37, 99, 235]);
      yPos += 10;

      addText('Standard HTTP statuskoder:', 11, true);
      yPos += 5;
      const errorCodes = [
        '200: Suksess',
        '201: Opprettet',
        '400: Ugyldig forespørsel',
        '401: Ikke autentisert',
        '403: Ingen tilgang',
        '404: Ikke funnet',
        '429: For mange forespørsler',
        '500: Serverfeil'
      ];

      errorCodes.forEach(code => {
        checkNewPage(8);
        addText(`• ${code}`, 9);
        yPos += 5;
      });

      // Footer on each page
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Side ${i} av ${totalPages} • Kompetanseattest Kvalitetssikring • ${new Date().toLocaleDateString('nb-NO')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      pdf.save(`API-dokumentasjon-${new Date().toISOString().split('T')[0]}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Kunne ikke generere PDF. Vennligst prøv igjen.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="flex gap-6">
      {/* Side Navigation */}
      <div className="w-64 flex-shrink-0">
        <div className="bg-white rounded-lg shadow-sm border p-4 sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BookOpen size={20} />
              Dokumentasjon
            </h2>
          </div>
          <nav className="space-y-1">
            {canSee('user-guide') && (
              <NavButton
                active={activeSection === 'user-guide'}
                onClick={() => setActiveSection('user-guide')}
                icon={<GraduationCap size={18} />}
              >
                Brukerveiledning
              </NavButton>
            )}
            {canSee('overview') && (
              <NavButton
                active={activeSection === 'overview'}
                onClick={() => setActiveSection('overview')}
                icon={<BookOpen size={18} />}
              >
                Oversikt
              </NavButton>
            )}
            {canSee('architecture') && (
              <NavButton
                active={activeSection === 'architecture'}
                onClick={() => setActiveSection('architecture')}
                icon={<Server size={18} />}
              >
                Systemarkitektur
              </NavButton>
            )}
            {canSee('api') && (
              <NavButton
                active={activeSection === 'api'}
                onClick={() => setActiveSection('api')}
                icon={<Key size={18} />}
              >
                API-dokumentasjon
              </NavButton>
            )}
            {canSee('database') && (
              <NavButton
                active={activeSection === 'database'}
                onClick={() => setActiveSection('database')}
                icon={<Database size={18} />}
              >
                Database-skjema
              </NavButton>
            )}
            {canSee('code-examples') && (
              <NavButton
                active={activeSection === 'code-examples'}
                onClick={() => setActiveSection('code-examples')}
                icon={<Code2 size={18} />}
              >
                Kodeeksempler
              </NavButton>
            )}
            {canSee('deployment') && (
              <NavButton
                active={activeSection === 'deployment'}
                onClick={() => setActiveSection('deployment')}
                icon={<GitBranch size={18} />}
              >
                Deployment
              </NavButton>
            )}
            {canSee('security') && (
              <NavButton
                active={activeSection === 'security'}
                onClick={() => setActiveSection('security')}
                icon={<Shield size={18} />}
              >
                Sikkerhet
              </NavButton>
            )}
            {canSee('troubleshooting') && (
              <NavButton
                active={activeSection === 'troubleshooting'}
                onClick={() => setActiveSection('troubleshooting')}
                icon={<AlertTriangle size={18} />}
              >
                Feilsøking
              </NavButton>
            )}
            {/* Integrasjoner - expandable group */}
            {canSee('integrations-overview') && (
              <>
                <button
                  onClick={() => setIntegrationsExpanded(!integrationsExpanded)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    ['integrations-overview', 'moodle', 'tutorlms', 'learndash', 'xtramile'].includes(activeSection)
                      ? 'bg-kf-primary/10 text-kf-primary'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {integrationsExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <Link2 size={18} />
                  <span className="flex-1 text-left">Integrasjoner</span>
                </button>
                {integrationsExpanded && (
                  <div className="ml-6 space-y-1 border-l-2 border-gray-200 pl-2">
                    <NavButton
                      active={activeSection === 'integrations-overview'}
                      onClick={() => setActiveSection('integrations-overview')}
                      icon={null}
                    >
                      Oversikt
                    </NavButton>
                    {canSee('moodle') && (
                      <NavButton
                        active={activeSection === 'moodle'}
                        onClick={() => setActiveSection('moodle')}
                        icon={null}
                      >
                        Moodle
                      </NavButton>
                    )}
                    {canSee('tutorlms') && (
                      <NavButton
                        active={activeSection === 'tutorlms'}
                        onClick={() => setActiveSection('tutorlms')}
                        icon={null}
                      >
                        TutorLMS
                      </NavButton>
                    )}
                    {canSee('learndash') && (
                      <NavButton
                        active={activeSection === 'learndash'}
                        onClick={() => setActiveSection('learndash')}
                        icon={null}
                      >
                        LearnDash
                      </NavButton>
                    )}
                    {canSee('xtramile') && (
                      <NavButton
                        active={activeSection === 'xtramile'}
                        onClick={() => setActiveSection('xtramile')}
                        icon={null}
                      >
                        Xtramile
                      </NavButton>
                    )}
                  </div>
                )}
              </>
            )}
          </nav>

          <button
            onClick={handleExportPDF}
            disabled={isGeneratingPDF}
            className="w-full mt-4 btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Genererer PDF...
              </>
            ) : (
              <>
                <Download size={18} />
                Eksporter PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          {canSee(activeSection) && activeSection === 'user-guide' && <UserGuideSection />}
          {canSee(activeSection) && activeSection === 'overview' && <OverviewSection />}
          {canSee(activeSection) && activeSection === 'architecture' && <ArchitectureSection />}
          {canSee(activeSection) && activeSection === 'api' && <ApiDocSection />}
          {canSee(activeSection) && activeSection === 'database' && <DatabaseSection />}
          {canSee(activeSection) && activeSection === 'code-examples' && <CodeExamplesSection />}
          {canSee(activeSection) && activeSection === 'deployment' && <DeploymentSection />}
          {canSee(activeSection) && activeSection === 'security' && <SecuritySection />}
          {canSee(activeSection) && activeSection === 'troubleshooting' && <TroubleshootingSection />}
          {canSee(activeSection) && activeSection === 'integrations-overview' && <IntegrationsOverviewSection />}
          {canSee(activeSection) && activeSection === 'moodle' && <LmsGuideSection lmsType="moodle" />}
          {canSee(activeSection) && activeSection === 'tutorlms' && <LmsGuideSection lmsType="tutorlms" />}
          {canSee(activeSection) && activeSection === 'learndash' && <LmsGuideSection lmsType="learndash" />}
          {canSee(activeSection) && activeSection === 'xtramile' && <XtramileSection />}
        </div>
      </div>
    </div>
  );
}

function IntegrationsOverviewSection() {
  const guides = Object.values(INTEGRATION_GUIDES);
  return (
    <div className="prose max-w-none">
      <h1>Integrasjoner – oversikt</h1>
      <p className="lead">
        Kompetanseattest støtter integrasjon med flere eksterne LMS-systemer (Learning Management Systems).
        Når et medlem kobler seg til et av disse, kan kursfullføringer automatisk utløse attestutstedelse.
      </p>

      <h2>Støttede LMS-systemer</h2>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {guides.map(g => (
          <div key={g.lmsType} className="border rounded-lg p-4">
            <h3 className="font-semibold text-lg">{g.displayName}</h3>
            <p className="text-sm text-gray-600 mt-1">{g.tagline}</p>
            <p className="text-xs text-gray-500 mt-2">Oppsett ca. {g.estimatedTime}</p>
          </div>
        ))}
      </div>

      <h2>Generell flyt</h2>
      <p>Uansett hvilket LMS som brukes, følger integrasjonen samme mønster:</p>
      <ol>
        <li><strong>Oppsett av tilkobling:</strong> Medlem legger inn API-legitimasjon under Min organisasjon → Integrasjoner</li>
        <li><strong>Avtaler:</strong> Medlem bekrefter avtaler om dataflyt, kvalitetskontroll og vurdering</li>
        <li><strong>Godkjenning:</strong> Studieforbund godkjenner forespørselen og signert PDF-avtale sendes til alle parter</li>
        <li><strong>Kurskoblinger:</strong> LMS-kurs kobles til godkjente studieplaner (1-til-1 eller 1-til-mange)</li>
        <li><strong>Synkronisering:</strong> Bakgrunnsjobb henter fullføringer fra LMS hver 5.–15. minutt</li>
        <li><strong>Attestutstedelse:</strong> Automatisk utstedelse eller klaim-flyt via Vipps avhengig av verifiseringsnivå</li>
      </ol>

      <h2>Vurderingstyper</h2>
      <p>Attesten viser hvilket nivå av kvalitetssikring fullføringen har:</p>
      <ul>
        <li><strong>external_sensor</strong> – Ekstern sensor har vurdert</li>
        <li><strong>internal_assessor</strong> – Intern vurderer har vurdert (f.eks. kursleder)</li>
        <li><strong>controlled_exam</strong> – Kontrollert eksamen (proctored)</li>
        <li><strong>unverified</strong> – Kun deltakelse/fullføring uten vurdering</li>
      </ul>

      <h2>Neste steg</h2>
      <p>Velg ditt LMS i menyen til venstre for detaljert dokumentasjon og steg-for-steg-veiledning for oppsett.</p>
    </div>
  );
}

function LmsGuideSection({ lmsType }: { lmsType: LmsType }) {
  const guide = INTEGRATION_GUIDES[lmsType];
  const hasFunctions = guide.requiredFunctions && guide.requiredFunctions.length > 0;

  return (
    <div className="prose max-w-none">
      <div className="not-prose flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold mb-1">{guide.displayName}</h1>
          <p className="text-gray-600 italic">{guide.tagline}</p>
        </div>
        <a
          href={`/api/integration-guides/${guide.lmsType}/pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded hover:bg-kf-primary/90"
        >
          <Download size={16} />
          Last ned som PDF
        </a>
      </div>

      <p className="lead">{guide.description}</p>

      <div className="not-prose bg-gray-50 rounded-lg p-3 mb-6 text-sm text-gray-700">
        <strong>⏱ Estimert oppsettstid:</strong> {guide.estimatedTime}
      </div>

      <h2>Forutsetninger</h2>
      <ul>
        {guide.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
      </ul>

      <h2>Steg-for-steg-oppsett</h2>
      {guide.steps.map((step, i) => (
        <div key={i} className="not-prose mb-4 pl-4 border-l-4 border-kf-primary">
          <h3 className="font-semibold text-lg mb-2">
            <span className="inline-flex items-center justify-center w-6 h-6 bg-kf-primary text-white rounded-full text-sm mr-2">{i + 1}</span>
            {step.title}
          </h3>
          <div className="ml-8 text-gray-700" style={{ whiteSpace: 'pre-wrap' }}>
            {step.body.split('\n').map((line, j) => (
              <p key={j} className="mb-1" dangerouslySetInnerHTML={{
                __html: line
                  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                  .replace(/`([^`]+?)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-sm font-mono">$1</code>')
              }} />
            ))}
            {step.note && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
                <strong>Merk:</strong> {step.note}
              </div>
            )}
          </div>
        </div>
      ))}

      {hasFunctions && (
        <>
          <h2>API-funksjoner</h2>
          <div className="not-prose">
            <p className="text-sm font-medium text-red-700 mb-2">🔴 Påkrevde:</p>
            <ul className="mb-3">
              {guide.requiredFunctions!.filter(f => f.required).map(f => (
                <li key={f.name} className="text-sm">
                  <code className="px-1 py-0.5 bg-gray-100 rounded font-mono">{f.name}</code>
                  <span className="ml-2 text-gray-700">— {f.description}</span>
                </li>
              ))}
            </ul>
            {guide.requiredFunctions!.some(f => !f.required) && (
              <>
                <p className="text-sm font-medium text-amber-700 mb-2">🟡 Anbefalt:</p>
                <ul>
                  {guide.requiredFunctions!.filter(f => !f.required).map(f => (
                    <li key={f.name} className="text-sm">
                      <code className="px-1 py-0.5 bg-gray-100 rounded font-mono">{f.name}</code>
                      <span className="ml-2 text-gray-700">— {f.description}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}

      <h2>Vanlige feil og løsninger</h2>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2">Feilmelding</th>
              <th className="text-left p-2">Årsak</th>
              <th className="text-left p-2">Løsning</th>
            </tr>
          </thead>
          <tbody>
            {guide.commonErrors.map((err, i) => (
              <tr key={i} className="border-b">
                <td className="p-2"><code className="text-xs">{err.error}</code></td>
                <td className="p-2 text-gray-700">{err.cause}</td>
                <td className="p-2">{err.solution}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Sikkerhet og beste praksis</h2>
      <ul>
        {guide.securityTips.map((tip, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: tip }} />
        ))}
      </ul>

      <div className="not-prose mt-6 p-4 bg-gray-50 rounded">
        <a
          href={guide.officialDocsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-kf-primary hover:underline"
        >
          <ExternalLink size={14} />
          {guide.officialDocsLabel}
        </a>
      </div>
    </div>
  );
}

function NavButton({ active, onClick, icon, children }: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition ${
        active
          ? 'bg-kf-primary text-white'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

// Hjelpekomponenter for pedagogisk innhold
function GuideTip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-4 rounded-r">
      <div className="flex gap-2">
        <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">{children}</div>
      </div>
    </div>
  );
}

function GuideWarn({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 my-4 rounded-r">
      <div className="flex gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">{children}</div>
      </div>
    </div>
  );
}

function GuideInfo({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-gray-50 border-l-4 border-gray-300 p-4 my-4 rounded-r">
      <div className="flex gap-2">
        <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700">{children}</div>
      </div>
    </div>
  );
}

function GuideStep({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 my-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-kf-primary text-white font-bold flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 mt-0 mb-2">{title}</h4>
        <div className="text-sm text-gray-700">{children}</div>
      </div>
    </div>
  );
}

function GuideSectionHeader({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h2 className="flex items-center gap-2 mt-12 pt-6 border-t border-gray-200 text-2xl font-bold text-gray-900">
      <span className="text-kf-primary">{icon}</span>
      {children}
    </h2>
  );
}

function UserGuideSection() {
  return (
    <div className="prose prose-sm max-w-none">
      <div className="bg-gradient-to-r from-kf-primary to-kf-secondary text-white p-6 rounded-lg mb-8 not-prose">
        <h1 className="text-3xl font-bold mb-2 text-white">Brukerveiledning</h1>
        <p className="text-white/90 mb-0">
          En komplett innføring i Kompetanseattest Kvalitetssikring — fra studieplan til utstedt kompetanseattest.
        </p>
      </div>

      <h2 id="velkommen">Velkommen som kursleverandør</h2>

      <p className="text-base">
        Som kursleverandør i et studieforbund godkjent av Kunnskapsdepartementet kan du utvikle
        studieplaner, få dem kvalitetssikret og utstede formelle kompetanseattester.
      </p>

      <p>
        Attestene følger <strong>EU Micro-credentials Framework</strong> og kan deles digitalt med
        arbeidsgivere via en kompetanselommebok som kandidaten styrer selv.
      </p>

      <h3>De fire rollene i systemet</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-6 not-prose">
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <strong className="block text-blue-900 mb-1">Kursleverandør</strong>
          <span className="text-sm text-blue-800">
            Det er <strong>deg</strong>. Du oppretter studieplaner, prøver, og utsteder attester.
          </span>
        </div>
        <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
          <strong className="block text-purple-900 mb-1">Studieforbund</strong>
          <span className="text-sm text-purple-800">
            Administrasjonen som godkjenner medlemskap og overprøver KI-validering ved behov.
          </span>
        </div>
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <strong className="block text-amber-900 mb-1">Sensor</strong>
          <span className="text-sm text-amber-800">
            Vurderer åpne prøvebesvarelser ved sensurering.
          </span>
        </div>
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <strong className="block text-green-900 mb-1">Kandidat</strong>
          <span className="text-sm text-green-800">
            Privatperson som mottar attesten via Vipps-pålogging på www.kompetanseattest.no.
          </span>
        </div>
      </div>

      <h3>Den gylne tråd: fra idé til kompetanseattest</h3>

      <p>Hele systemet er bygget rundt sju steg som følger naturlig etter hverandre:</p>

      <div className="space-y-3 my-6 not-prose">
        {[
          { n: 1, title: 'Studieplan', desc: 'Beskriver hva kurset skal lære bort, hvordan det vurderes, og hvor lenge det varer.' },
          { n: 2, title: 'KI-validering', desc: 'Kontrollerer kvaliteten — gir score 0–100 og konkrete forbedringsforslag.' },
          { n: 3, title: 'Prøvesett', desc: 'Bygger på spørsmål KI-en genererte. Kobles til én bestemt versjon av studieplanen.' },
          { n: 4, title: 'Kursinstans', desc: 'Samler kandidater rundt ett prøvesett med konkrete datoer, sted og evt. møte-URL.' },
          { n: 5, title: 'Påmelding', desc: 'Kandidater meldes på (manuelt, CSV eller Vipps-selvpåmelding via kompetanseheving.no). Venteliste når fullt.' },
          { n: 6, title: 'Oppmøte og eksamen', desc: 'Hak av oppmøte → start eksamen kun for fremmøtte. Anonym for sensor.' },
          { n: 7, title: 'Kompetanseattest', desc: 'Utstedes automatisk ved bestått, hentes av kandidaten via Vipps.' }
        ].map((s) => (
          <div key={s.n} className="flex gap-4 items-start border-l-4 border-kf-primary bg-gray-50 rounded-r-lg p-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-kf-primary text-white text-sm font-bold flex items-center justify-center">
              {s.n}
            </div>
            <div className="flex-1">
              <strong className="text-gray-900">{s.title}</strong>
              <p className="text-sm text-gray-700 m-0 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<FileText className="w-7 h-7" />}>1. Studieplaner og litteratur</GuideSectionHeader>

      <h3>Hva er en studieplan?</h3>
      <p>
        En studieplan er en formell beskrivelse av et opplæringstilbud. Den inneholder mål, innhold,
        vurderingsformer og krav for kandidaten. Studieplanen er fundamentet for alle eksamener og
        kompetanseattester knyttet til kurset.
      </p>

      <h3>Slik oppretter du en studieplan</h3>
      <p>Gå til <strong>Studieplaner og litteratur</strong> i toppmenyen, klikk <em>Ny studieplan</em>.</p>

      <GuideStep n={1} title="Grunninfo">
        Tittel, beskrivelse, varighet (timer), studiepoeng (ECTS), og EQF/NKR-nivå (1–8).
        Norsk er primærspråket — engelsk genereres automatisk under KI-validering.
      </GuideStep>

      <GuideStep n={2} title="Målgruppe og opptakskrav">
        Beskriv hvem kurset retter seg mot og hva som forventes av forkunnskaper.
        Du kan koble både målgruppe og opptakskrav til ESCO-yrkesroller for å gjøre studieplanen søkbar i EU.
      </GuideStep>

      <GuideStep n={3} title="Læringsutbytter">
        Tre kategorier: <strong>kunnskap</strong>, <strong>ferdigheter</strong>, og <strong>generell kompetanse</strong>.
        Hvert utbytte skal være målbart med et <em>aktivt verb</em> (analysere, anvende, utføre, vurdere,
        sammenligne, beregne…) — ikke passive formuleringer som "forstå" eller "kjenne til".
        Du kan koble hvert utbytte til ESCO-ferdigheter — systemet vil foreslå relevante ferdigheter
        automatisk basert på teksten du skriver.
      </GuideStep>

      <GuideStep n={4} title="Innholdsmoduler">
        Bryt kurset ned i moduler med tittel, beskrivelse, antall timer og hvilke læringsutbytter
        modulen leverer. Sum av modul-timer skal samsvare med totalt antall timer i grunninfo.
      </GuideStep>

      <GuideStep n={5} title="Vurderingsformer">
        Beskriv hvordan kandidaten vurderes — flervalgsprøve, åpne spørsmål, praktisk innlevering,
        samt vurderingskriterier (hva skal til for å bestå).
      </GuideStep>

      <GuideStep n={6} title="Litteratur">
        Legg til pensum og fordypningslitteratur. Du kan velge fra fellesbiblioteket
        (delt med andre kursleverandører i ditt studieforbund), laste opp egne PDF/Word, eller
        peke til en URL. Systemet kontrollerer at du har lisens for å bruke materialet.
      </GuideStep>

      <GuideTip>
        Ved siden av manuelt arbeid kan KI-en foreslå litteratur, ESCO-ferdigheter og forbedringer.
        Bruk knappen <em>KI-forslag</em> over hvert felt der den er tilgjengelig. Forslag er alltid
        bare forslag — du må selv akseptere dem.
      </GuideTip>

      <h3>KI-validering: hvordan det fungerer</h3>

      <p>
        Når studieplanen er ferdig redigert, klikker du <strong>Send til KI-validering</strong>.
        Systemet kjører to operasjoner i sekvens:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 not-prose">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <strong>1. Engelsk oversettelse</strong>
          </div>
          <p className="text-sm text-gray-700 m-0">
            Hele innholdet oversettes automatisk til engelsk — kun hvis ikke allerede gjort.
          </p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <strong>2. Validering på begge språk</strong>
          </div>
          <p className="text-sm text-gray-700 m-0">
            Begge versjoner vurderes mot ni kvalitetskriterier (se under).
          </p>
        </div>
      </div>

      <h4 className="mt-6">De ni vurderingskriteriene</h4>

      <p>
        Hvert kriterium gir en delscore som summeres til totalscore (0–100).
        Læringsutbytter er det viktigste — det teller for 25 av 100 mulige poeng.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 my-4 not-prose">
        {[
          { name: 'Læringsutbytter (kvalitet)', detail: 'Bloom-taksonomi, målbarhet', weight: 25 },
          { name: 'Konstruktiv samordning', detail: 'Utbytter ↔ innhold ↔ vurdering', weight: 15 },
          { name: 'Arbeidsbelastning', detail: 'Timer ↔ ECTS (25–30t per ECTS)', weight: 10 },
          { name: 'Strukturell kvalitet', detail: 'Modulorganisering, fullstendighet', weight: 10 },
          { name: 'Egnethet for kompetanseattest', detail: 'Passer formatet?', weight: 10 },
          { name: 'Metadata-samsvar', detail: 'EU Micro-credentials, ELM v3', weight: 10 },
          { name: 'Praktiske ferdigheter', detail: 'Vurderbarhet av "kan gjøre"', weight: 10 },
          { name: 'Myke ferdigheter', detail: 'Observerbar praksis', weight: 5 },
          { name: 'Narrativ attesttekst', detail: 'Grunnlag for autotekst', weight: 5 }
        ].map((c) => (
          <div key={c.name} className="border border-gray-200 rounded p-3 bg-gray-50 flex justify-between items-start gap-3">
            <div>
              <strong className="text-sm block">{c.name}</strong>
              <span className="text-xs text-gray-600">{c.detail}</span>
            </div>
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-kf-primary/10 text-kf-primary text-xs font-bold">
              {c.weight} pt
            </span>
          </div>
        ))}
      </div>

      <h4 className="mt-6">Tre mulige konklusjoner</h4>

      <p>
        Basert på totalscore får studieplanen én av tre konklusjoner. Standardgrensene kan justeres
        av studieforbundet, men dette er det vanlige:
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4 not-prose">
        <div className="border border-green-300 bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-green-700" />
            <strong className="text-green-900">Score ≥ 80</strong>
          </div>
          <p className="text-sm text-green-800 m-0">
            <strong>Selvgodkjenning aktivert.</strong> Du kan godkjenne studieplanen direkte —
            ingen manuell innsending til studieforbundet kreves.
          </p>
        </div>
        <div className="border border-amber-300 bg-amber-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            <strong className="text-amber-900">Score 60–79</strong>
          </div>
          <p className="text-sm text-amber-800 m-0">
            <strong>Avvist med revisjonsforslag.</strong> Du kan sende inn med begrunnelse —
            studieforbundet vurderer manuelt og kan godkjenne ved override.
          </p>
        </div>
        <div className="border border-red-300 bg-red-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-700" />
            <strong className="text-red-900">Score &lt; 60</strong>
          </div>
          <p className="text-sm text-red-800 m-0">
            <strong>Avvist.</strong> Studieplanen må forbedres og valideres på nytt før den kan brukes.
          </p>
        </div>
      </div>

      <GuideTip>
        Etter validering ser du detaljerte tilbakemeldinger per kategori med konkrete forbedringsforslag.
        Læringsutbytter som er flagget av KI-en kan oppdateres direkte med ett klikk på <em>Bruk forslag</em>.
      </GuideTip>

      <h3>Versjonering: når du oppdaterer en godkjent studieplan</h3>
      <p>
        Når du redigerer en studieplan som allerede er godkjent og brukt i utstedte attester,
        oppretter systemet automatisk en ny versjon ved neste godkjenning. Hver versjon har sitt eget
        versjonsnummer (v1.0, v2.0, …) og holder fast på innholdet som var aktivt da en attest ble utstedt —
        slik at attester aldri "endres bak ryggen" på kandidaten.
      </p>

      <h3>Flerspråklig (norsk/engelsk)</h3>
      <p>
        Alle godkjente studieplaner får automatisk engelsk oversettelse via KI under valideringsflyt.
        Du kan finjustere oversettelsen i <em>Oversettelseseditor</em> som vises i studieplan-skjemaet
        når engelsk versjon er generert. Attester som utstedes basert på studieplanen er tilgjengelig
        på begge språk for kandidaten.
      </p>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<ClipboardList className="w-7 h-7" />}>2. Spørsmål, prøvesett og kursinstans</GuideSectionHeader>

      <h3>Spørsmålsbank</h3>
      <p>
        Når en studieplan blir godkjent, genererer KI-en automatisk forslag til spørsmål basert på
        læringsutbyttene. Disse havner i <em>spørsmålsbanken</em> (se <em>Studieplaner</em> →{' '}
        <em>Spørsmål</em>) hvor du gjennomgår, redigerer og godkjenner dem før de kan brukes i prøver.
      </p>

      <p>Spørsmålene kommer i fire typer:</p>
      <ul>
        <li><strong>Flervalg</strong> — ett rett svar fra fire alternativer</li>
        <li><strong>Flersvar</strong> — flere riktige svar (som "kryss av alle som passer")</li>
        <li><strong>Sant/usant</strong> — to alternativer</li>
        <li><strong>Åpent svar</strong> — fritekst som krever sensorvurdering</li>
      </ul>

      <GuideTip>
        Hvert spørsmål er tagget med ett eller flere læringsutbytter. Det gjør at prøvesettet automatisk
        får en balansert dekning når du velger spørsmål — du kan ikke ende opp med en prøve som bare
        tester ett av åtte læringsutbytter.
      </GuideTip>

      <GuideWarn>
        En modul kan ikke lagres uten minst ett læringsutbytte (i kunnskap, ferdigheter eller
        generell kompetanse). En studieplan med moduler kan ikke godkjennes hvis noen av modulene
        mangler utbytter. Et prøvesett kan ikke opprettes hvis valgte moduler mangler utbytter.
        Dette sikrer at attesten alltid har et reelt faglig grunnlag.
      </GuideWarn>

      <h3>Lag et prøvesett</h3>
      <p>Gå til <strong>Kurs og prøver</strong> → fane <em>Prøvesystem</em> → <em>Nytt prøvesett</em>.</p>

      <GuideStep n={1} title="Velg studieplan og versjon">
        Prøvesettet må alltid kobles til én bestemt versjon av studieplanen. Hvis studieplanen
        oppdateres senere, må du eksplisitt velge å migrere prøvesettet til ny versjon.
      </GuideStep>

      <GuideStep n={2} title="Velg omfang (Hele studieplanen eller utvalg moduler)">
        <p className="m-0 mb-2">
          Hvis studieplanen er delt opp i moduler, kan du velge om prøven skal dekke{' '}
          <strong>hele studieplanen</strong> eller bare <strong>et utvalg moduler</strong>.
          Når du velger et utvalg, vil kompetanseattesten kun reflektere læringsutbyttene i de
          valgte modulene — tittelen blir for eksempel{' '}
          <em>"Bærekraft i praksis — Modul 1: FNs bærekraftsmål"</em>.
        </p>
        <p className="m-0">
          Når du har valgt modulene, viser systemet aggregert ECTS og antall utbytter for det valgte
          omfanget.
        </p>
      </GuideStep>

      <GuideStep n={3} title="Velg spørsmål">
        Plukk fra godkjente spørsmål i spørsmålsbanken. Systemet anbefaler en miks som dekker alle
        læringsutbyttene i det valgte omfanget. Du kan også velge om kandidaten ser tilfeldige
        spørsmål fra en pool (anti-juks).
      </GuideStep>

      <GuideStep n={4} title="Konfigurer">
        Tidsbegrensning, antall forsøk, beståttgrense (f.eks. 70 %), tilrettelegging,
        og om prøven skal kjøres i Safe Exam Browser (SEB).
      </GuideStep>

      <GuideStep n={5} title="Vurderingsmåte">
        <ul className="m-0">
          <li><strong>Automatisk:</strong> alle spørsmål er flervalg/flersvar/sant-usant — score regnes ut umiddelbart</li>
          <li><strong>Sensor:</strong> minst ett åpent svar — sensor må gjennomgå før attest utstedes</li>
        </ul>
      </GuideStep>

      <h3>Opprett en kursinstans</h3>
      <p>
        En <em>kursinstans</em> er den faktiske gjennomføringen — en bestemt gruppe kandidater på
        bestemte datoer som tar et bestemt prøvesett. Gå til <strong>Kurs og prøver</strong> → fane{' '}
        <em>Kurs</em> → <em>Nytt kurs</em>.
      </p>

      <p className="font-semibold mt-4 mb-2">Slik fyller du ut skjemaet:</p>

      <div className="space-y-3 my-4 not-prose">
        <div className="border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-3">
          <strong className="text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> Prøvesett
          </strong>
          <p className="text-sm text-gray-700 m-0 mt-1">
            Når du velger et prøvesett ser du straks et info-kort med <em>faglig grunnlag</em> —
            hvilken studieplan og hvilke moduler som inngår, ECTS og antall læringsutbytter.
            Klikk "Vis studieplan og inkluderte moduler" for fullstendig oversikt over
            læringsutbytter per modul.
          </p>
        </div>
        <div className="border-l-4 border-green-500 bg-green-50/50 rounded-r-lg p-3">
          <strong className="text-gray-900 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Tidspunkt og sted
          </strong>
          <p className="text-sm text-gray-700 m-0 mt-1">
            Start- og sluttdato, eksamen-tidspunkt (utløser SEB-test-utsendelse 48t og 24t før),
            maks antall kandidater, adresse/sted.
          </p>
        </div>
        <div className="border-l-4 border-purple-500 bg-purple-50/50 rounded-r-lg p-3">
          <strong className="text-gray-900 flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Møte-URL og møte-instruksjoner
          </strong>
          <p className="text-sm text-gray-700 m-0 mt-1">
            Hvis kurset er digitalt, lagrer du Teams-/Zoom-/Meet-lenken her. Lenken brukes
            automatisk som plassholder <code className="bg-white px-1 rounded">{'{mote_url}'}</code>{' '}
            i meldingsmaler du sender til kandidatene. Møte-instruksjoner kan inneholde passord,
            forberedelse, hvilken plattform osv.
          </p>
        </div>
        <div className="border-l-4 border-orange-500 bg-orange-50/50 rounded-r-lg p-3">
          <strong className="text-gray-900 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Påmelding via kompetanseheving.no
          </strong>
          <p className="text-sm text-gray-700 m-0 mt-1">
            Krys av "Vis på kompetanseheving.no" for å gjøre kursinstansen tilgjengelig for
            offentlig selvpåmelding via Vipps. Du kan sette påmeldingsgebyr, selvavmeldingsfrist
            (default 30 dager før kursstart), avbestillingsgebyr og no-show-gebyr.
            Når instansen er full, plasseres nye kandidater automatisk på venteliste.
          </p>
        </div>
      </div>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<Users className="w-7 h-7" />}>3. Kandidater og eksamen</GuideSectionHeader>

      <p className="text-base">
        Når kursinstansen er klar, åpner du den ved å klikke på instansen i kurs-listen. Du
        kommer da til <strong>kursinstans-detaljsiden</strong> som har fire faner.
      </p>

      <h3>De fire fanene</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4 not-prose">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="w-4 h-4 text-gray-700" />
            <strong>Oversikt</strong>
          </div>
          <p className="text-sm text-gray-600 m-0">
            Nøkkeltall: antall kandidater, status, bestått/ikke bestått, eksamen-tidspunkt og kursinformasjon.
          </p>
        </div>
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-blue-700" />
            <strong className="text-blue-900">Kandidater</strong>
          </div>
          <p className="text-sm text-blue-800 m-0">
            Påmeldte, venteliste, registrering (manuell/CSV) og "Send melding"-broadcast.
          </p>
        </div>
        <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
          <div className="flex items-center gap-2 mb-1">
            <PlayCircle className="w-4 h-4 text-yellow-700" />
            <strong className="text-yellow-900">Prøve</strong>
          </div>
          <p className="text-sm text-yellow-800 m-0">
            Oppmøte-avkrysning og "Start eksamen for fremmøtte". Erstatter den gamle invitasjon-flyten.
          </p>
        </div>
        <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="w-4 h-4 text-purple-700" />
            <strong className="text-purple-900">Sensorer</strong>
          </div>
          <p className="text-sm text-purple-800 m-0">
            Tildel sensor til kurset (for prøver med åpne svar).
          </p>
        </div>
      </div>

      <h3>Kandidater-fanen — tre måter å få kandidater på</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 not-prose">
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Download className="w-5 h-5 text-blue-700" />
            <strong className="text-blue-900">CSV-import</strong>
          </div>
          <p className="text-sm text-blue-800 m-0 mb-2">
            Last opp en liste i ett kjør — egnet for store grupper.
          </p>
          <div className="text-xs text-blue-700 bg-white/60 rounded p-2 font-mono">
            navn, mobilnummer, e-post
          </div>
        </div>

        <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-purple-700" />
            <strong className="text-purple-900">Manuelt</strong>
          </div>
          <p className="text-sm text-purple-800 m-0">
            Legg til én og én kandidat. Egnet for små kurs eller tester.
          </p>
        </div>

        <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-orange-700" />
            <strong className="text-orange-900">Vipps-selvpåmelding</strong>
          </div>
          <p className="text-sm text-orange-800 m-0">
            Kandidater melder seg på selv via kompetanseheving.no. Kreves at instansen er
            synlig der.
          </p>
        </div>
      </div>

      <h3 className="mt-8">Venteliste</h3>

      <p>
        Når en kursinstans har <strong>maks antall kandidater</strong> satt og blir full,
        plasseres nye kandidater automatisk på venteliste (med posisjon #1, #2, osv.).
        Ventelisten vises i en egen seksjon under påmeldte. Klikk{' '}
        <strong>Promoter</strong> for å flytte en kandidat opp til påmeldt — kandidaten får
        bekreftelses-e-post og SMS.
      </p>

      <h3>Send melding til kandidater (broadcast)</h3>

      <p>
        Klikk <strong>Send melding</strong>-knappen øverst i Kandidater-fanen for å sende SMS
        og/eller e-post til en utvalgt målgruppe — for eksempel Teams-lenke ved kursstart,
        påminnelser, eller praktisk informasjon.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4 not-prose">
        <div className="border border-gray-200 rounded p-3 bg-white">
          <strong className="text-sm flex items-center gap-1 mb-1">
            <MessageSquare className="w-4 h-4" /> Maler å velge mellom
          </strong>
          <ul className="text-xs text-gray-700 m-0 mt-1 list-disc list-inside">
            <li>Kursstart-informasjon (krever møte-URL)</li>
            <li>Påminnelse om kurs</li>
            <li>Påminnelse om eksamen</li>
            <li>Egendefinert melding (fri tekst)</li>
          </ul>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-white">
          <strong className="text-sm flex items-center gap-1 mb-1">
            <Mail className="w-4 h-4" /> Plassholdere
          </strong>
          <p className="text-xs text-gray-700 m-0 mt-1">
            Bruk{' '}
            <code className="bg-gray-100 px-1 rounded">{'{navn}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{kursnavn}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{mote_url}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{mote_instruksjoner}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{eksamen_dato}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{kandidatkode}'}</code>{' '}
            <code className="bg-gray-100 px-1 rounded">{'{arrangor}'}</code>{' '}
            — disse erstattes per mottaker.
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-700">
        Du velger målgruppe (påmeldte / venteliste / fremmøtte / ikke møtt — eller kombinasjoner),
        kanal (e-post og/eller SMS), og kan se forhåndsvisning før utsendelse. Hele utsendelsen
        loggføres for audit-formål.
      </p>

      <h3 className="mt-8">Prøve-fanen — slik gjennomfører du eksamen</h3>

      <p>
        Den gamle "Send invitasjoner til alle"-flyten er erstattet med en oppmøte-basert flyt
        som sikrer at kun kandidater som faktisk er til stede får eksamenslenken. Dette gjelder
        både fysiske og digitale kurs.
      </p>

      <div className="space-y-3 my-6 not-prose">
        <div className="flex gap-4 items-start border-l-4 border-yellow-500 bg-yellow-50/50 rounded-r-lg p-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-600 text-white text-sm font-bold flex items-center justify-center">
            1
          </div>
          <div className="flex-1">
            <strong className="text-gray-900">Hak av oppmøte</strong>
            <p className="text-sm text-gray-700 m-0 mt-0.5">
              Krys av hver kandidat som er til stede. Du kan også bruke "Hak av alle" eller
              "Fjern alle" for å spare tid.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start border-l-4 border-green-500 bg-green-50/50 rounded-r-lg p-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold flex items-center justify-center">
            2
          </div>
          <div className="flex-1">
            <strong className="text-gray-900">Klikk "Start eksamen for X fremmøtte"</strong>
            <p className="text-sm text-gray-700 m-0 mt-0.5">
              Vi sender SEB-eksamenslenke og kandidatkode på e-post og SMS til de fremmøtte.
              Lenken er unik per kandidat.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start border-l-4 border-red-500 bg-red-50/50 rounded-r-lg p-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center">
            3
          </div>
          <div className="flex-1">
            <strong className="text-gray-900">Ikke-fremmøtte håndteres separat</strong>
            <p className="text-sm text-gray-700 m-0 mt-0.5">
              Kandidater som ikke ble huket av som fremmøtt får status "ikke møtt" og kan flyttes
              til ny instans senere — eventuelt med no-show-gebyr hvis det er aktivert på instansen.
            </p>
          </div>
        </div>
      </div>

      <GuideInfo>
        Status-kortene øverst viser sanntids-tall: Totalt / Fremmøtt / Ikke markert / Prøve påbegynt
        / Prøve fullført. Kandidater som allerede har påbegynt eller fullført eksamen vises i
        en egen del av tabellen.
      </GuideInfo>

      <h3>Slik tar kandidaten prøven</h3>

      <p>Fra invitasjonslenke til ferdig resultat — fire steg:</p>

      <div className="space-y-3 my-6 not-prose">
        {[
          { n: 1, title: 'Klikk invitasjonslenken', desc: 'Kommer rett til prøvesiden uten innlogging (helt anonymt).' },
          { n: 2, title: 'Bekreft identitet', desc: 'Skriver inn navn og fødselsdato. Systemet logger dette for sporbarhet.' },
          { n: 3, title: 'Ta prøven', desc: 'Innenfor tidsbegrensningen som er satt på prøvesettet.' },
          { n: 4, title: 'Resultat', desc: 'Auto-vurdert prøve viser resultat umiddelbart. Sensor-prøver venter til sensor har frigitt.' }
        ].map((s) => (
          <div key={s.n} className="flex gap-4 items-start border-l-4 border-green-500 bg-green-50/50 rounded-r-lg p-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold flex items-center justify-center">
              {s.n}
            </div>
            <div className="flex-1">
              <strong className="text-gray-900">{s.title}</strong>
              <p className="text-sm text-gray-700 m-0 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <GuideTip>
        Hvis prøven krever Safe Exam Browser (SEB), sender systemet ut SEB-test-lenker 48 timer
        og 24 timer før eksamen-tidspunktet for å sikre at kandidatene har installert og testet
        SEB på forhånd.
      </GuideTip>

      <h3>Tilrettelegging</h3>

      <p>
        Kandidater med spesielle behov kan be om tilrettelegging når de mottar invitasjonen.
        Forespørselen sendes til deg eller en annen administrator i organisasjonen for godkjenning.
      </p>

      <p className="font-semibold mt-4 mb-2">Eksempler på tilrettelegging:</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-4 not-prose">
        <div className="border border-gray-200 rounded p-2 bg-gray-50 text-center text-sm">
          ⏱ Utvidet tid
        </div>
        <div className="border border-gray-200 rounded p-2 bg-gray-50 text-center text-sm">
          🔊 Skjermleser
        </div>
        <div className="border border-gray-200 rounded p-2 bg-gray-50 text-center text-sm">
          🔍 Forstørrelse
        </div>
        <div className="border border-gray-200 rounded p-2 bg-gray-50 text-center text-sm">
          📝 Annen tilpasning
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Når forespørselen er godkjent, justeres prøveinnstillingene automatisk for den aktuelle
        kandidaten — du trenger ikke gjøre mer.
      </p>

      <h3>Sensor-flyt (åpne spørsmål)</h3>

      <p>
        Hvis prøvesettet inneholder åpne spørsmål, må en sensor vurdere besvarelsen før kandidaten
        får resultat. Slik fungerer flyten:
      </p>

      <div className="space-y-3 my-6 not-prose">
        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">
            1
          </div>
          <div>
            <strong className="text-gray-900">Kandidat leverer prøven</strong>
            <p className="text-sm text-gray-700 m-0">Besvarelsen havner i kø for sensor.</p>
          </div>
        </div>
        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">
            2
          </div>
          <div>
            <strong className="text-gray-900">Sensor logger inn</strong>
            <p className="text-sm text-gray-700 m-0">
              På <em>Sensor Dashboard</em> — egen rolle adskilt fra kursleverandør.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center justify-center">
            3
          </div>
          <div>
            <strong className="text-gray-900">Sensor vurderer</strong>
            <p className="text-sm text-gray-700 m-0">
              Hvert åpne svar får Bestått/Ikke bestått eller poengscore.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-3 bg-white">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-600 text-white text-sm font-bold flex items-center justify-center">
            4
          </div>
          <div>
            <strong className="text-gray-900">Resultat frigis</strong>
            <p className="text-sm text-gray-700 m-0">
              Kandidaten får varsel og kan se resultatet. Hvis bestått, utstedes attesten automatisk.
            </p>
          </div>
        </div>
      </div>

      <GuideTip>
        Sensorer registreres som egen brukerkonto (rollen <em>sensor</em>) og tildeles kurs fra{' '}
        <em>Min organisasjon → Sensorer</em>. Én sensor kan vurdere mange kurs på tvers av
        kursinstanser.
      </GuideTip>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<Award className="w-7 h-7" />}>4. Kompetanseattest</GuideSectionHeader>

      <p className="text-base">
        Kompetanseattesten er det endelige resultatet — et formelt dokument som bekrefter at
        kandidaten har fullført kurset og oppnådd læringsutbyttene.
      </p>

      <h3>Når utstedes attesten?</h3>
      <p>Attesten utstedes <strong>automatisk</strong> i én av to situasjoner:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 not-prose">
        <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-5 h-5 text-blue-700" />
            <strong className="text-blue-900">Via eksamen i Kompetanseattest</strong>
          </div>
          <p className="text-sm text-blue-800 m-0">
            Når kandidaten har bestått prøven — enten auto-vurdert eller frigitt av sensor.
          </p>
        </div>
        <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-5 h-5 text-purple-700" />
            <strong className="text-purple-900">Via LMS-fullføring</strong>
          </div>
          <p className="text-sm text-purple-800 m-0">
            Når et tilkoblet LMS rapporterer at kurset er fullført. Se kapittel 5.
          </p>
        </div>
      </div>

      <h3>Hva inneholder attesten?</h3>
      <p>Hver attest samler informasjon i tre kategorier:</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 not-prose">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h4 className="font-semibold text-gray-900 mt-0 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-600" />
            Identitet
          </h4>
          <ul className="text-sm space-y-1 m-0 list-disc pl-4">
            <li>Fullt navn</li>
            <li>Fødselsdato</li>
          </ul>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h4 className="font-semibold text-gray-900 mt-0 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-600" />
            Læring og innhold
          </h4>
          <ul className="text-sm space-y-1 m-0 list-disc pl-4">
            <li>Kursnavn og versjon</li>
            <li>Studietimer og ECTS</li>
            <li>EQF/NKR-nivå (1–8)</li>
            <li>Læringsutbytter</li>
            <li>ESCO-ferdigheter</li>
            <li>Vurderingsmåte</li>
          </ul>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h4 className="font-semibold text-gray-900 mt-0 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-gray-600" />
            Verifisering
          </h4>
          <ul className="text-sm space-y-1 m-0 list-disc pl-4">
            <li>Utstedende organisasjon</li>
            <li>Studieforbund</li>
            <li>Offentlig verifiseringslenke</li>
            <li>QR-kode</li>
          </ul>
        </div>
      </div>

      <h3>Sikkerhet — tre lag</h3>
      <p>
        Attestene er bygget for å være tamper-proof og maskinlesbare. PDF-en har tre lag med beskyttelse:
      </p>

      <div className="space-y-3 my-6 not-prose">
        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-kf-primary text-white font-bold flex items-center justify-center">
            1
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mt-0 mb-1">Digital signatur</h4>
            <p className="text-sm text-gray-700 m-0">
              Hele dokumentet signeres med et <strong>eSeal-sertifikat</strong> fra Buypass.
              Det betyr at enhver endring i innholdet — selv ett enkelt tegn — bryter signaturen
              og gjør attesten ugyldig.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-kf-primary text-white font-bold flex items-center justify-center">
            2
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mt-0 mb-1">256-bit AES-kryptering</h4>
            <p className="text-sm text-gray-700 m-0">
              PDF-en er kryptert mot uautoriserte endringer. Forsøk på "unlock" via tjenester som
              iLovePDF vil i beste fall produsere en ny, usignert PDF som ikke består verifisering.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-kf-primary text-white font-bold flex items-center justify-center">
            3
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mt-0 mb-1">Maskinlesbar EDC JSON-LD</h4>
            <p className="text-sm text-gray-700 m-0">
              Innebygd i PDF-en ligger en W3C Verifiable Credential (EDC-format). Dette gjør at
              automatiske systemer hos arbeidsgivere eller utdanningsinstitusjoner kan verifisere
              og lese attesten programmatisk — ikke bare visuelt.
            </p>
          </div>
        </div>
      </div>

      <GuideTip>
        Verifiseringssiden viser tydelig om en PDF er endret eller mangler signatur. Lær opp
        kandidatene dine til å skanne QR-koden eller åpne verifiseringslenken — det er den eneste
        sikre måten å bekrefte at attesten er ekte.
      </GuideTip>

      <h3>Kandidatens kompetanselommebok</h3>

      <p>
        Når attesten er utstedt, mottar kandidaten en e-post med en lenke. Etter Vipps-pålogging på{' '}
        <a href="https://www.kompetanseattest.no" target="_blank" rel="noopener noreferrer">
          www.kompetanseattest.no
        </a>{' '}
        får de en personlig kompetanselommebok.
      </p>

      <p className="font-semibold mt-4">I lommeboken kan kandidaten:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4 not-prose">
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">📚 Samle alle attester</strong>
          <span className="text-xs text-gray-600">Fra alle kursleverandører i ett system</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">📥 Laste ned PDF</strong>
          <span className="text-xs text-gray-600">På norsk eller engelsk</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">🎓 Importere vitnemål</strong>
          <span className="text-xs text-gray-600">Fra Vitnemålsportalen (høyere utdanning)</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">📄 Bygge CV</strong>
          <span className="text-xs text-gray-600">Alle kompetanser samlet automatisk</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">🧭 Få kursforslag</strong>
          <span className="text-xs text-gray-600">Læringssti basert på profil og attester</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">🔗 Dele med arbeidsgiver</strong>
          <span className="text-xs text-gray-600">Sikker, signert lenke</span>
        </div>
      </div>

      <h3>Offentlig verifisering</h3>

      <p>
        Hver attest har en <strong>kort offentlig URL</strong> og en <strong>QR-kode</strong>.
      </p>

      <p>
        Når en arbeidsgiver skanner QR-koden eller besøker URL-en, vises en lett-leselig
        verifiseringsside. Siden bekrefter at attesten er autentisk og ikke er trukket tilbake —
        uten å avsløre kandidatens private detaljer.
      </p>

      <GuideInfo>
        Verifiseringssiden er åpen for alle og krever ikke innlogging. Den er bevisst designet
        slik at en arbeidsgiver kan sjekke en attest på 10 sekunder uten å lage konto.
      </GuideInfo>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<Link2 className="w-7 h-7" />}>5. LMS-integrasjoner</GuideSectionHeader>

      <p>
        Hvis du allerede bruker et læringsstyringssystem (LMS) for å levere kursene dine, kan
        Kompetanseattest koble seg til og automatisk utstede attest når en kandidat fullfører
        kurset i LMS-en. Da slipper kandidaten å ta en separat eksamen hos oss.
      </p>

      <p>Vi støtter fire LMS-systemer i dag:</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4 not-prose">
        <div className="border rounded-lg p-4">
          <strong>Moodle</strong>
          <p className="text-sm text-gray-600 m-0 mt-1">Klassisk åpen kildekode, vidt utbredt i utdanningssektoren.</p>
        </div>
        <div className="border rounded-lg p-4">
          <strong>TutorLMS</strong>
          <p className="text-sm text-gray-600 m-0 mt-1">WordPress-basert. Krever <em>TutorLMS Pro</em> for API-tilgang.</p>
        </div>
        <div className="border rounded-lg p-4">
          <strong>LearnDash</strong>
          <p className="text-sm text-gray-600 m-0 mt-1">WordPress-basert. Fungerer i alle lisensplaner.</p>
        </div>
        <div className="border rounded-lg p-4">
          <strong>Xtramile</strong>
          <p className="text-sm text-gray-600 m-0 mt-1">Norsk LMS rettet mot næringsliv og fagopplæring.</p>
        </div>
      </div>

      <GuideTip>
        Detaljerte oppsettsguider per LMS finner du i menyen til venstre under <em>Integrasjoner</em>.
        Hver guide tar deg gjennom hvordan du oppretter API-tilgang i LMS-en og kobler den til
        Kompetanseattest.
      </GuideTip>

      <h3>Hvordan flyten fungerer</h3>
      <ol>
        <li>Du oppretter en LMS-integrasjon under <em>Min organisasjon</em> → <em>Integrasjoner</em>.</li>
        <li>Studieforbundet ditt godkjenner integrasjonen (compliance med kvalitetskrav).</li>
        <li>Du kobler LMS-kurs til godkjente studieplaner på siden <em>LMS-kurs</em> i hovedmenyen.</li>
        <li>Når en kandidat fullfører et kurs i LMS-en, oppdager Kompetanseattest dette via API.</li>
        <li>Systemet sender en e-post til kandidaten med en lenke for å hente attesten.</li>
        <li>Kandidaten logger inn med Vipps og henter attesten i sin kompetanselommebok.</li>
      </ol>

      <h3>Kobling mellom LMS-kurs og studieplan</h3>
      <p>
        For at attesten skal vise riktige læringsutbytter og ESCO-ferdigheter, må hvert LMS-kurs
        kobles til en godkjent studieplan. Du gjør dette på <strong>LMS-kurs</strong>-siden i
        hovedmenyen (vises kun hvis du har en godkjent integrasjon).
      </p>

      <GuideStep n={1} title="Klikk &quot;Legg til kobling&quot; på riktig LMS">
        Modalen som åpnes er tilpasset LMS-en din — Moodle, TutorLMS og LearnDash henter live
        kursliste fra ditt LMS-system slik at du kan velge kurs direkte. Xtramile bruker manuell
        kurs-ID.
      </GuideStep>

      <GuideStep n={2} title="Velg studieplan og modul-omfang">
        Velg hvilken studieplan kurset skal kobles til. Hvis studieplanen er delt opp i moduler,
        kan du velge om koblingen dekker hele studieplanen eller bare et utvalg moduler — det
        styrer hva attesten viser når kandidaten fullfører kurset.
      </GuideStep>

      <GuideStep n={3} title="Legg inn URL-en til studieplanen i kursbeskrivelsen">
        Vi krever at LMS-kurset har en URL til den godkjente studieplanen i sin beskrivelse —
        dette er et compliance-krav som dokumenterer hva attesten faktisk forplikter seg til.
        Modalen viser deg eksakt URL og hvor du skal lime den inn.
      </GuideStep>

      <GuideStep n={4} title="Velg vurderingstype og bekreft kvalitet">
        Tre vurderingstyper kan velges (se under). I tillegg må du krysse av{' '}
        <em>"kandidatene ikke foretar egenvurdering"</em>, og hvis du velger{' '}
        <em>kontrollert eksamen</em>, må du også bekrefte at eksamen gjennomføres under
        kontrollerte forhold (SEB eller fysisk tilsyn).
      </GuideStep>

      <h3>Vurderingstyper i LMS</h3>

      <p>
        Attesten viser hvilken type vurdering som ligger bak. Alle tre er likeverdige når de
        utføres etter retningslinjene — kompetanseattest krever uansett at noen har vurdert
        kandidatens læringsutbytter.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-6 not-prose">
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-700" />
            <strong className="text-green-900">Ekstern sensor</strong>
          </div>
          <p className="text-sm text-green-800 m-0">
            En sensor utenfor kursleverandøren har vurdert kandidatens læringsutbytter.
          </p>
        </div>
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-700" />
            <strong className="text-green-900">Intern vurderer</strong>
          </div>
          <p className="text-sm text-green-800 m-0">
            En kvalifisert vurderer hos kursleverandøren har vurdert kandidaten.
          </p>
        </div>
        <div className="border border-green-200 bg-green-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-700" />
            <strong className="text-green-900">Kontrollert eksamen</strong>
          </div>
          <p className="text-sm text-green-800 m-0">
            Bestått eksamen via SEB (identitetskontroll + låst miljø) eller fysisk eksamen
            med tilsyn.
          </p>
        </div>
      </div>

      <GuideWarn>
        Verdien <em>unverified</em> (kun aktivitetsfullføring uten vurdering) kan ikke lenger
        velges ved nye koblinger — en kompetanseattest forutsetter at noe faktisk vurderes.
        Eksisterende mappinger med denne verdien blir automatisk konvertert til{' '}
        <em>Ekstern sensor</em> ved redigering.
      </GuideWarn>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<Globe className="w-7 h-7" />}>6. Min organisasjon</GuideSectionHeader>

      <p className="text-base">
        Hver kursleverandør-organisasjon har én eller flere brukerkontoer. Det finnes to nivåer for
        medlemmer: ordinær <strong>medlem</strong> og <strong>medlem-administrator</strong>.
      </p>

      <h3>De to nivåene for medlemmer</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6 not-prose">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5 text-gray-700" />
            <strong className="text-gray-900">Medlem</strong>
          </div>
          <p className="text-sm text-gray-700 m-0 mb-2">
            Den ordinære rollen for kursleverandørens ansatte. Fokus på fagarbeidet.
          </p>
          <p className="text-sm font-semibold text-gray-800 mb-1">Kan:</p>
          <ul className="text-xs text-gray-700 m-0 pl-4 space-y-0.5 list-disc">
            <li>Opprette og redigere studieplaner</li>
            <li>Sende studieplaner til KI-validering</li>
            <li>Lage prøvesett og kursinstanser</li>
            <li>Invitere kandidater og se attester</li>
            <li>Administrere LMS-integrasjoner</li>
          </ul>
        </div>

        <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-purple-700" />
            <strong className="text-purple-900">Medlem-administrator</strong>
          </div>
          <p className="text-sm text-purple-800 m-0 mb-2">
            En medlem som i tillegg har ansvar for brukerstell i organisasjonen.
          </p>
          <p className="text-sm font-semibold text-purple-900 mb-1">Kan også:</p>
          <ul className="text-xs text-purple-800 m-0 pl-4 space-y-0.5 list-disc">
            <li>Se liste over alle brukere i organisasjonen</li>
            <li>Invitere nye brukere (medlem eller sensor)</li>
            <li>Aktivere/deaktivere brukere</li>
            <li>Endre rolle (medlem ↔ sensor)</li>
            <li>Utpeke flere medlem-administratorer</li>
            <li>Foreslå e-postdomener for organisasjonen</li>
          </ul>
        </div>
      </div>

      <GuideTip>
        Det <strong>må alltid være minst én medlem-administrator</strong> i organisasjonen. Hvis du
        er den eneste, må du først utpeke en ny før du kan fjerne din egen administrator-status.
      </GuideTip>

      <h3>Hvem blir medlem-administrator?</h3>
      <ul>
        <li>Den første brukeren studieforbundet inviterer er automatisk medlem-administrator</li>
        <li>Eksisterende medlem-administrator kan utpeke andre via "Brukere"-tab i organisasjonen</li>
        <li>Studieforbundet kan også gjøre denne endringen ved behov</li>
      </ul>

      <h3>Hvor finner jeg disse funksjonene?</h3>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
        <p className="font-semibold text-blue-900 mb-2 mt-0">Som medlem-administrator har du tre tabs i toppmenyen:</p>
        <ol className="m-0 pl-5 text-sm text-blue-900 space-y-1">
          <li><strong>Min organisasjon</strong> — informasjon om organisasjonen</li>
          <li><strong>Brukere</strong> — liste, invitasjon, og administrasjon av kollegaer</li>
          <li><strong>E-postdomener</strong> — foreslå godkjente domener</li>
        </ol>
        <p className="text-xs text-blue-800 mt-2 mb-0">
          Vanlig medlem ser kun "Min organisasjon"-fanen.
        </p>
      </div>

      <h3>Invitere en ny bruker</h3>

      <p>
        Som medlem-administrator: gå til <strong>Brukere</strong>-tabben og klikk{' '}
        <em>Inviter bruker</em>. Du fyller inn:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4 not-prose">
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">📧 E-postadresse</strong>
          <span className="text-xs text-gray-600">Må tilhøre et godkjent domene (sensorer kan ha ekstern e-post)</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-gray-50">
          <strong className="block text-sm">🎭 Rolle</strong>
          <span className="text-xs text-gray-600">Medlem (fagarbeid) eller Sensor (prøvevurdering)</span>
        </div>
        <div className="border border-gray-200 rounded p-3 bg-purple-50">
          <strong className="block text-sm">🛡 Administrator?</strong>
          <span className="text-xs text-gray-600">Valgfritt — krever studieforbund-godkjenning. Se under.</span>
        </div>
      </div>

      <p>
        Den inviterte mottar e-post med en bekreftelseslenke og setter sitt eget passord ved første pålogging.
      </p>

      <h3>Forespørsel om medlem-admin-rolle</h3>

      <p>
        For å hindre at administrasjonsrettigheter sprer seg ukontrollert, krever medlem-admin-rollen{' '}
        <strong>godkjenning fra studieforbundet</strong> når en medlem-administrator ber om det.
      </p>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 my-4">
        <p className="font-semibold text-purple-900 mt-0 mb-2">Slik fungerer godkjenningsflyten:</p>
        <ol className="m-0 pl-5 text-sm text-purple-900 space-y-1">
          <li>Du krysser av "Gi administrator-rettigheter" når du inviterer (eller klikker promote-knappen for en eksisterende bruker)</li>
          <li>Brukeren blir invitert / forblir aktiv som <strong>vanlig medlem</strong> inntil videre</li>
          <li>Studieforbundet og systemadmin får e-post + dashboard-varsel om forespørselen</li>
          <li>Når studieforbundet godkjenner — får brukeren medlem-admin-rolle automatisk</li>
          <li>Hvis avvist — du får e-post med begrunnelse</li>
        </ol>
      </div>

      <GuideInfo>
        <strong>Unntak:</strong> Studieforbund og systemadmin kan utpeke medlem-admin direkte uten godkjenningsflyt.
        Det er kun medlem-admin sine egne forespørsler som krever godkjenning.
      </GuideInfo>

      <h3>E-postdomener — hvorfor og hvordan</h3>

      <p>
        For å hindre at brukere registreres med private e-poster (som blir vanskelig å følge opp når
        ansatte slutter), må alle brukere inviteres med e-post fra <strong>godkjente domener</strong>.
      </p>

      <p>Slik fungerer flyten:</p>

      <div className="space-y-3 my-6 not-prose">
        {[
          { n: 1, title: 'Foreslå domene', desc: 'Du foreslår et domene (f.eks. dittfirma.no) under E-postdomener-tabben.' },
          { n: 2, title: 'Studieforbundet får varsel', desc: 'På e-post og som badge i admin-panelet.' },
          { n: 3, title: 'Godkjenning eller avvisning', desc: 'Studieforbundet vurderer og godkjenner eller avviser med begrunnelse.' },
          { n: 4, title: 'Du får svar', desc: 'På e-post — godkjent betyr du nå kan invitere brukere fra dette domenet.' }
        ].map((s) => (
          <div key={s.n} className="flex gap-4 items-start border-l-4 border-purple-500 bg-purple-50/50 rounded-r-lg p-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white text-sm font-bold flex items-center justify-center">
              {s.n}
            </div>
            <div className="flex-1">
              <strong className="text-gray-900">{s.title}</strong>
              <p className="text-sm text-gray-700 m-0 mt-0.5">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <GuideWarn>
        <strong>Hard validering:</strong> Hvis et domene ikke er godkjent, kan invitasjon ikke
        sendes. Sensorer kan unntakvis inviteres med e-post fra domener som ikke er godkjent
        (siden de ofte er innleide).
      </GuideWarn>

      {/* ======================================================== */}
      <GuideSectionHeader icon={<Sparkles className="w-7 h-7" />}>7. Tips og vanlige spørsmål</GuideSectionHeader>

      <h3>Hvor finner jeg…?</h3>

      <p>En rask navigasjonsguide for de vanligste oppgavene:</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 my-6 not-prose">
        {[
          ['Mine studieplaner', 'Toppmeny → Studieplaner og litteratur → fane Studieplaner'],
          ['Felles litteraturbibliotek', 'Toppmeny → Studieplaner og litteratur → fane Litteraturbibliotek'],
          ['Spørsmålsbank for en plan', 'Klikk på studieplanen → knappen "Spørsmålsbank"'],
          ['Lisenser for litteratur', 'Klikk på studieplanen → knappen "Lisenser"'],
          ['Mine prøvesett', 'Toppmeny → Kurs og prøver → fane Prøvesystem'],
          ['Mine kursinstanser', 'Toppmeny → Kurs og prøver → fane Kurs'],
          ['Påmeldte og venteliste', 'Inne i en kursinstans → fane Kandidater'],
          ['Oppmøte og start-eksamen', 'Inne i en kursinstans → fane Prøve'],
          ['Send melding til kandidater', 'Inne i en kursinstans → Kandidater → "Send melding"'],
          ['Møte-URL (Teams/Zoom)', 'Når du redigerer kursinstansen'],
          ['Koble LMS-kurs til studieplan', 'Toppmeny → LMS-kurs'],
          ['Utstedte attester', 'Inne i en kursinstans → fane Kandidater (status "Attest utstedt")'],
          ['LMS-integrasjoner', 'Min organisasjon → Integrasjoner'],
          ['API-nøkler', 'Min organisasjon → fane API-tilkoblinger'],
          ['Mine enheter', 'Innstillinger → Mine enheter']
        ].map(([what, where]) => (
          <div key={what} className="border border-gray-200 rounded p-3 bg-white">
            <strong className="block text-sm text-gray-900">{what}</strong>
            <span className="text-xs text-gray-600">{where}</span>
          </div>
        ))}
      </div>

      <h3 className="mt-10">Vanlige spørsmål</h3>

      <div className="space-y-4 my-6 not-prose">
        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hva er den nye Prøve-fanen, og hvor ble "Send invitasjoner" av?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              Den gamle "Send invitasjoner til alle"-flyten er erstattet av en oppmøte-basert flyt
              i den nye Prøve-fanen. Du må først hake av hvilke kandidater som faktisk møter opp
              (digitalt eller fysisk), og deretter klikke <strong>"Start eksamen for X fremmøtte"</strong>.
              Da sendes eksamenslenken kun til de som er til stede.
            </p>
            <p className="m-0">
              Dette sikrer at attest ikke utstedes til kandidater som ikke deltok, og at no-shows
              kan håndteres separat (eventuelt med gebyr eller flytt til ny dato).
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hvordan sender jeg Teams-lenke til kandidatene før kursstart?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              Først: rediger kursinstansen og legg inn <strong>møte-URL</strong> (Teams/Zoom/Meet)
              og eventuelle møte-instruksjoner. Disse lagres på instansen.
            </p>
            <p className="m-0">
              Deretter: gå til <strong>Kandidater</strong>-fanen → <strong>Send melding</strong>.
              Velg malen "Kursstart-informasjon", og lenken settes automatisk inn via plassholderen{' '}
              <code>{'{mote_url}'}</code>. Velg målgruppe (vanligvis "Påmeldte") og kanal
              (e-post + SMS), og send.
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Kan kandidater melde seg på selv?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              Ja. Krys av <strong>"Vis på kompetanseheving.no"</strong> når du oppretter eller
              redigerer kursinstansen. Da blir den synlig på kompetanseheving.no for offentlig
              selvpåmelding via Vipps.
            </p>
            <p className="m-0">
              Du kan sette påmeldingsgebyr, selvavmeldingsfrist (default 30 dager før kursstart),
              avbestillingsgebyr ved sen avmelding og no-show-gebyr. Når maks antall er nådd,
              plasseres nye påmeldte automatisk på venteliste.
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hva er Karrierepasset, og berører det meg som kursarrangør?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              <strong>Karrierepasset</strong> (Synchrocity AS) er en ekstern tjeneste hvor
              kandidater kan ha personlighet- og karriereinteresse-profil (MBTI, RIASEC).
              Kandidater kan koble sin Kompetanseattest-konto til Karrierepasset for å:
            </p>
            <ul className="m-0 mt-1 pl-5">
              <li>Få beriket CV-bygger med personlighetsprofil</li>
              <li>Forbedret matching i Læringssti basert på interesser</li>
              <li>Få sine kompetanseattester og jobberfaring synket til Karrierepasset</li>
            </ul>
            <p className="m-0 mt-2">
              Som kursarrangør trenger du <strong>ikke</strong> gjøre noe — integrasjonen er
              kandidat-styrt og krever ingen handling fra din side. Funksjonen aktiveres etter
              avtale med Synchrocity.
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hvorfor må en modul ha læringsutbytter for at jeg skal kunne lagre den?
          </summary>
          <p className="text-sm text-gray-700 m-0">
            En kompetanseattest dokumenterer at kandidaten har oppnådd spesifikke læringsutbytter.
            Hvis modulen er tom, blir attesten innholdsløs. Systemet blokkerer derfor på tre
            nivåer: modulen kan ikke lagres uten utbytter, studieplanen kan ikke godkjennes hvis
            noen modul mangler utbytter, og prøvesett kan ikke opprettes med tomme moduler.
            Hver modul må ha minst ett utbytte i kunnskap, ferdigheter eller generell kompetanse.
          </p>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hva betyr scoren under KI-validering?
          </summary>
          <p className="text-sm text-gray-700 m-0">
            Score er en samlet vurdering av studieplanens kvalitet på en skala 0–100. Den er
            beregnet ut fra ni vektede kriterier — læringsutbytter teller mest med 25 av 100
            mulige poeng. Standardgrensene er <strong>80</strong> for selvgodkjenning og{' '}
            <strong>60</strong> for manuell innsending, men disse kan justeres av studieforbundet
            ditt.
          </p>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hva gjør jeg hvis KI-validering gir lav score?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              Les tilbakemeldingene grundig — KI-en peker konkret på hvilke læringsutbytter, moduler
              eller vurderingskriterier som har mangler.
            </p>
            <p className="m-0">
              Du kan ofte fikse mange problemer på én gang ved å oppdatere læringsutbytter til å
              bruke <strong>aktive verb</strong> (analysere, vurdere, utføre) i stedet for{' '}
              <strong>passive verb</strong> (forstå, kjenne til, være kjent med).
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Kandidater rapporterer at de ikke får invitasjons-e-post
          </summary>
          <div className="text-sm text-gray-700 space-y-2">
            <p className="m-0">Tre ting å sjekke i denne rekkefølgen:</p>
            <ol className="m-0 pl-5 space-y-1">
              <li>Be kandidaten sjekke <strong>søppelpost-mappen</strong>.</li>
              <li>Hvis ikke der, kontakt <strong>studieforbundet</strong> — kan være et SMTP-problem som må fikses sentralt.</li>
              <li>Som backup: kopier invitasjonslenken manuelt fra kandidatlisten og send selv.</li>
            </ol>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            En attest er utstedt med feil opplysninger — hva gjør jeg?
          </summary>
          <div className="text-sm text-gray-700">
            <p>
              Du kan <strong>trekke tilbake</strong> attesten under kursinstansen. Da blir den
              merket som ugyldig på den offentlige verifiseringssiden, og kandidaten varsles.
              Etterpå kan du utstede en korrigert versjon.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-2">
              <strong className="text-amber-900">⚠ Tilbaketrekking er sporbar</strong>{' '}
              <span className="text-amber-800">og bør kun brukes ved reelle feil.</span>
            </div>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Kan jeg slette en studieplan?
          </summary>
          <p className="text-sm text-gray-700 m-0">
            Bare studieplaner i status <em>utkast</em> eller <em>avvist</em> kan slettes. Hvis en
            studieplan er godkjent og brukt til attester, er den frosset for sporbarhet — du kan
            markere den som <em>arkivert</em>, men ikke slette den.
          </p>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hvordan fungerer KI-forslag for ESCO?
          </summary>
          <div className="text-sm text-gray-700">
            <p className="mt-0">
              <strong>ESCO</strong> (European Skills, Competences, Qualifications and Occupations)
              er EU sin standardiserte klassifisering av ferdigheter og yrker.
            </p>
            <p className="m-0">
              Når du skriver et læringsutbytte, kan systemet foreslå relevante ESCO-ferdigheter via
              semantisk søk. Du velger selv hvilke som passer — KI-en bare foreslår.
            </p>
          </div>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Kan jeg bruke samme studieplan på flere kursinstanser?
          </summary>
          <p className="text-sm text-gray-700 m-0">
            Ja — én studieplan kan ha mange prøvesett, og hvert prøvesett kan brukes på mange
            kursinstanser. Det er anbefalt å lage prøvesett som varianter (lett / normal / vanskelig)
            i stedet for å ha mange like.
          </p>
        </details>

        <details className="border border-gray-200 rounded-lg p-4 bg-white group">
          <summary className="font-semibold cursor-pointer text-gray-900 group-open:mb-3">
            Hva skjer hvis jeg endrer en godkjent studieplan?
          </summary>
          <p className="text-sm text-gray-700 m-0">
            Innholdet markeres som <em>endret etter godkjenning</em> og må re-valideres før det kan
            brukes til nye attester. Eksisterende attester knyttet til forrige versjon forblir
            gyldige på sin opprinnelige versjon.
          </p>
        </details>
      </div>

      <div className="mt-12 pt-6 border-t border-gray-200 bg-gray-50 -mx-8 -mb-8 px-8 py-6 rounded-b-lg">
        <h3 className="mt-0 mb-2 text-base">Trenger du mer hjelp?</h3>
        <p className="text-sm text-gray-600 m-0">
          Kontakt studieforbundet ditt direkte. De kjenner systemet best og kan eskalere til
          Kompetanseforbundet ved behov.
        </p>
      </div>
    </div>
  );
}

function OverviewSection() {
  return (
    <div className="prose max-w-none">
      <h1>Kompetanseattest Kvalitetssikring - Systemdokumentasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <BookOpen className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Velkommen til systemdokumentasjonen</h3>
            <p className="mt-2 text-sm text-blue-700">
              Denne dokumentasjonen gir en omfattende oversikt over Kompetanseattest Kvalitetssikring-systemet,
              inkludert arkitektur, API-er, database-struktur og deployment-prosedyrer.
            </p>
          </div>
        </div>
      </div>

      <h2>Om systemet</h2>
      <p>
        Kompetanseattest Kvalitetssikring er et omfattende system utviklet for Kompetanseforbundet
        (studieforbund godkjent av Kunnskapsdepartementet). Systemet validerer studieplaner mot
        EU Micro-credentials Framework og ELMv3-standarder ved hjelp av kunstig intelligens.
      </p>

      <h3>Hovedfunksjoner</h3>
      <ul>
        <li><strong>AI-basert validering:</strong> Automatisk validering av studieplaner med OpenAI GPT-4</li>
        <li><strong>Rollestyring:</strong> Systemadmin, admin (studieforbund), member (organisasjon) og sensor</li>
        <li><strong>Godkjenningsflyt:</strong> Strukturert prosess for godkjenning/avvisning med offentlig verifisering</li>
        <li><strong>Prøvehåndtering:</strong> Komplett system for prøveopprettelse, gjennomføring og sensurering</li>
        <li><strong>API-integrasjon:</strong> RESTful API for eksterne LMS-systemer</li>
        <li><strong>PDF-eksport:</strong> Generering av godkjente studieplaner som PDF</li>
        <li><strong>Sikker autentisering:</strong> Enhet-basert autentisering med verifiseringskoder</li>
      </ul>

      <h3>Teknologi-stack</h3>
      <div className="grid grid-cols-2 gap-4 my-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-kf-primary mb-2">Frontend</h4>
          <ul className="text-sm space-y-1">
            <li>React 18 + TypeScript</li>
            <li>Vite (build tool)</li>
            <li>Tailwind CSS</li>
            <li>React Router</li>
            <li>jsPDF + html2canvas</li>
          </ul>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-kf-primary mb-2">Backend</h4>
          <ul className="text-sm space-y-1">
            <li>Node.js + Express</li>
            <li>PostgreSQL</li>
            <li>Socket.io</li>
            <li>Docker + Docker Compose</li>
            <li>Caddy (reverse proxy)</li>
          </ul>
        </div>
      </div>

      <h3>Systemkomponenter</h3>
      <ul>
        <li><strong>Frontend:</strong> React SPA med TypeScript</li>
        <li><strong>Email Service:</strong> Node.js backend for e-postvarsler og API</li>
        <li><strong>Database:</strong> PostgreSQL med Row Level Security (RLS)</li>
        <li><strong>Reverse Proxy:</strong> Caddy med automatisk HTTPS</li>
        <li><strong>Eksternt API:</strong> OpenAI for AI-validering</li>
      </ul>
    </div>
  );
}

function ArchitectureSection() {
  return (
    <div className="prose max-w-none">
      <h1>Systemarkitektur</h1>

      <h2>Overordnet arkitektur</h2>
      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`┌─────────────────────────────────────────────────────────────┐
│                    Internet / Brukere                        │
└─────────────────────┬───────────────────────────────────────┘
                      │
                      ▼
┌─────────────────────────────────────────────────────────────┐
│              Caddy Reverse Proxy (HTTPS)                     │
│  - studieplan.kompetanseattest.no                           │
│  - Automatisk SSL/TLS (Let's Encrypt)                       │
│  - Rate limiting                                             │
└──────────┬──────────────────────────────┬───────────────────┘
           │                              │
           ▼                              ▼
┌─────────────────────┐      ┌──────────────────────────────┐
│   Frontend (Nginx)  │      │  Email Service (Node.js)     │
│   - React SPA       │      │  - Express API               │
│   - Static files    │      │  - Socket.io                 │
│   - Port 80         │      │  - SMTP integration          │
└─────────────────────┘      │  - Port 3000                 │
                             └──────────┬───────────────────┘
                                        │
                                        ▼
                             ┌──────────────────────────────┐
                             │  PostgreSQL Database         │
                             │  - User data                 │
                             │  - Study plans               │
                             │  - Exam data                 │
                             │  - RLS policies              │
                             └──────────────────────────────┘

External Services:
- OpenAI API (AI validation)
- ProISP SMTP (Email delivery)
- QR Server API (QR code generation)`}
        </pre>
      </div>

      <h2>Docker Container Struktur</h2>
      <div className="bg-gray-50 p-4 rounded-lg my-4">
        <h3 className="font-semibold mb-2">Test Environment</h3>
        <ul className="text-sm space-y-1">
          <li><code>caddy-test</code> - Reverse proxy</li>
          <li><code>kvalitetssikring-frontend-test</code> - Frontend</li>
          <li><code>kvalitetssikring-email-test</code> - Email service</li>
          <li><code>supabase-db</code> - PostgreSQL database (shared)</li>
        </ul>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg my-4">
        <h3 className="font-semibold mb-2">Production Environment</h3>
        <ul className="text-sm space-y-1">
          <li><code>caddy-prod</code> - Reverse proxy</li>
          <li><code>kvalitetssikring-frontend-prod</code> - Frontend</li>
          <li><code>kvalitetssikring-email-prod</code> - Email service</li>
          <li><code>supabase-db</code> - PostgreSQL database (shared)</li>
        </ul>
      </div>

      <h2>Nettverksflyt</h2>
      <ol>
        <li><strong>Brukerforespørsel:</strong> HTTPS request til studieplan.kompetanseattest.no</li>
        <li><strong>Caddy:</strong> Terminerer SSL, ruter til frontend eller API</li>
        <li><strong>Frontend:</strong> Serverer React SPA, gjør API-kall til email service</li>
        <li><strong>Email Service:</strong> Håndterer autentisering, forretningslogikk, database-operasjoner</li>
        <li><strong>Database:</strong> PostgreSQL med RLS for sikkerhet</li>
      </ol>

      <h2>Dataflytsdiagram</h2>
      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`Bruker → Frontend → Email Service → Database
   ↓                      ↓
   └─────────────────→ OpenAI API (validering)
                          ↓
                    SMTP Server (e-post)`}
        </pre>
      </div>
    </div>
  );
}

function ApiDocSection() {
  const [apiType, setApiType] = useState<'external' | 'internal'>('external');
  const [expandedSection, setExpandedSection] = useState<string>('auth');

  return (
    <div className="prose max-w-none">
      <h1>API-dokumentasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <Key className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">To typer API-tilgang</h3>
            <p className="mt-2 text-sm text-blue-700">
              Systemet tilbyr to separate API-er med forskjellige autentiseringsmetoder og tilgangsnivåer.
            </p>
          </div>
        </div>
      </div>

      {/* API Type Selector */}
      <div className="flex gap-3 my-6 not-prose">
        <button
          onClick={() => setApiType('external')}
          className={`flex-1 px-6 py-4 rounded-lg border-2 transition text-left ${
            apiType === 'external'
              ? 'border-kf-primary bg-kf-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-lg mb-1">API-legitimasjon (Eksterne LMS)</div>
          <div className="text-sm text-gray-600">
            For eksterne LMS-systemer. Kun prøvefunksjonalitet (2 endepunkter).
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Autentisering: X-API-Username + X-API-Key headers
          </div>
        </button>
        <button
          onClick={() => setApiType('internal')}
          className={`flex-1 px-6 py-4 rounded-lg border-2 transition text-left ${
            apiType === 'internal'
              ? 'border-kf-primary bg-kf-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="font-semibold text-lg mb-1">Intern API (Brukerautentisering)</div>
          <div className="text-sm text-gray-600">
            For påloggede brukere. Full tilgang til alle funksjoner (36 endpoints).
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Autentisering: Bearer token (JWT) fra innlogging
          </div>
        </button>
      </div>

      {/* External LMS API Documentation */}
      {apiType === 'external' && (
        <>
          <h2>API-legitimasjon (Eksterne LMS-systemer)</h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Key className="h-5 w-5 text-yellow-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Opprett API-legitimasjon</h3>
                <p className="mt-2 text-sm text-yellow-700">
                  API-nøkler opprettes per medlemsorganisasjon under "API-tilgang" i menyen.
                  Hver nøkkel har et brukernavn og hemmelig nøkkel for autentisering.
                </p>
              </div>
            </div>
          </div>

          <h3>Base URL</h3>
          <div className="bg-gray-50 p-3 rounded font-mono text-sm my-4">
            https://studieplan.kompetanseattest.no/api/v1
          </div>

          <h3>Autentisering</h3>
          <p>Alle API-forespørsler krever følgende HTTP headers:</p>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
            <pre className="text-sm">
{`X-API-Username: [ditt-api-brukernavn]
X-API-Key: [din-hemmelige-nøkkel]
Content-Type: application/json`}
            </pre>
          </div>

          <h3>Rate Limiting</h3>
          <p>API-forespørsler er begrenset til <strong>1000 requests per time</strong> per API-nøkkel (konfigurerbart).</p>

          <h3>Tilgjengelige Endpoints</h3>

          <div className="space-y-8 mt-6">
            {/* POST /api/v1/exam/start */}
            <div className="border-l-4 border-kf-primary pl-4">
              <h4 className="text-lg font-semibold text-kf-primary">POST /api/v1/exam/start</h4>
              <p className="text-sm text-gray-600 mb-4">Start en prøvesesjon for en kandidat.</p>

              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
                <pre className="text-sm overflow-x-auto">{`// Request
{
  "course_link": "abc123def456",  // Unik kurslenke fra systemet
  "candidate_code": "student-12345",  // Studentnummer/ID fra LMS
  "extended_time_minutes": 30,  // Valgfritt: ekstra tid (tilrettelegging)
  "alternative_assessment": false,  // Valgfritt: alternativ vurdering
  "metadata": {  // Valgfritt: ekstra metadata
    "lms_course_id": "COURSE-2024-001",
    "lms_user_id": "user-789"
  }
}

// Response 200 OK
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "exam_url": "https://studieplan.kompetanseattest.no/take-exam/550e8400...",
  "expires_at": "2024-11-13T12:00:00Z",
  "time_limit_minutes": 120,
  "message": "Eksamen startet. Kandidaten kan nå ta eksamen via exam_url."
}

// Response 400 - Ugyldig forespørsel
{
  "success": false,
  "error": "Missing required fields",
  "message": "course_link and candidate_code are required"
}

// Response 404 - Kurs ikke funnet
{
  "success": false,
  "error": "Course not found",
  "message": "Invalid course_link or course not accessible"
}`}</pre>
              </div>

              <div className="bg-blue-50 p-3 rounded mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong> Lagre <code>session_id</code> og <code>exam_url</code> i ditt LMS.
                  Kandidaten bruker <code>exam_url</code> for å ta prøven.
                </p>
              </div>
            </div>

            {/* GET /api/v1/exam/result/:session_id */}
            <div className="border-l-4 border-kf-primary pl-4">
              <h4 className="text-lg font-semibold text-kf-primary">GET /api/v1/exam/result/:session_id</h4>
              <p className="text-sm text-gray-600 mb-4">Hent prøveresultat etter at kandidaten har levert.</p>

              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
                <pre className="text-sm overflow-x-auto">{`// Request (GET)
// URL: /api/v1/exam/result/550e8400-e29b-41d4-a716-446655440000
// Headers: X-API-Username, X-API-Key

// Response 200 OK - Eksamen levert og sensurert
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "candidate_code": "student-12345",
  "status": "graded",
  "score": 85,
  "max_score": 100,
  "percentage": 85,
  "passed": true,
  "pass_threshold": 60,
  "graded_at": "2024-11-13T11:45:00Z",
  "time_spent_minutes": 95,
  "answers": [
    {
      "question_number": 1,
      "question_text": "Forklar...",
      "answer": "Svaret her...",
      "score": 15,
      "max_score": 20,
      "feedback": "Godt svar, men..."
    }
  ]
}

// Response 200 OK - Eksamen ikke levert ennå
{
  "success": true,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "in_progress",
  "message": "Exam not yet submitted"
}

// Response 404 - Sesjon ikke funnet
{
  "success": false,
  "error": "Session not found"
}`}</pre>
              </div>

              <div className="bg-blue-50 p-3 rounded mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong> Poll dette endepunktet for å sjekke om kandidaten har levert prøven.
                  Status endres fra <code>in_progress</code> til <code>graded</code>.
                </p>
              </div>
            </div>
          </div>

          <h3 className="mt-8">Feilhåndtering</h3>
          <ul>
            <li><code>200</code> - Suksess</li>
            <li><code>400</code> - Ugyldig forespørsel (mangler påkrevde felt)</li>
            <li><code>401</code> - Ugyldig API-legitimasjon</li>
            <li><code>403</code> - IP-adresse ikke hvitelistet / Organisasjon suspendert</li>
            <li><code>404</code> - Kurs eller sesjon ikke funnet</li>
            <li><code>409</code> - Konflikt (kandidat har allerede aktiv sesjon)</li>
            <li><code>429</code> - Rate limit overskredet</li>
            <li><code>500</code> - Serverfeil</li>
          </ul>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Key className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Trenger flere endpoints?</h3>
                <p className="mt-2 text-sm text-green-700">
                  Vi kan utvide API-et med flere funksjoner basert på behov fra eksterne LMS-systemer.
                  Ta kontakt for å diskutere hvilke integrasjoner som er aktuelle.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Internal API Documentation */}
      {apiType === 'internal' && (
        <>
          <h2>Intern API (Brukerautentisering)</h2>

          <div className="bg-purple-50 border-l-4 border-purple-500 p-4 my-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Shield className="h-5 w-5 text-purple-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-purple-800">Kun for påloggede brukere</h3>
                <p className="mt-2 text-sm text-purple-700">
                  Disse endepunktene krever gyldig JWT token fra innlogging. Tilgang avhenger av brukerrolle
                  (member, admin, systemadmin).
                </p>
              </div>
            </div>
          </div>

          <h3>Base URL</h3>
          <div className="bg-gray-50 p-3 rounded font-mono text-sm my-4">
            https://studieplan.kompetanseattest.no
          </div>

          <h3>Autentisering</h3>
          <p>Alle forespørsler krever JWT token i Authorization header:</p>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
            <pre className="text-sm">
{`Authorization: Bearer [jwt-token-fra-innlogging]
Content-Type: application/json`}
            </pre>
          </div>

          <h3>Rate Limiting</h3>
          <p>Forskjellige grenser per endepunkt-type:</p>
          <ul>
            <li><strong>Generelle endepunkter:</strong> 100 requests/minutt</li>
            <li><strong>AI-endepunkter:</strong> 10 requests/minutt</li>
            <li><strong>Autentisering:</strong> 5 forsøk/15 minutter</li>
          </ul>

          <h3>API Endpoint Kategorier</h3>
          <div className="space-y-2 my-6">
            <ApiSectionButton
              active={expandedSection === 'auth'}
              onClick={() => setExpandedSection('auth')}
              title="Autentisering"
              count={5}
            />
            <ApiSectionButton
              active={expandedSection === 'study-plans'}
              onClick={() => setExpandedSection('study-plans')}
              title="Studieplaner"
              count={7}
            />
            <ApiSectionButton
              active={expandedSection === 'literature'}
              onClick={() => setExpandedSection('literature')}
              title="Litteratur"
              count={6}
            />
            <ApiSectionButton
              active={expandedSection === 'exams'}
              onClick={() => setExpandedSection('exams')}
              title="Avsluttende prøver"
              count={8}
            />
            <ApiSectionButton
              active={expandedSection === 'admin'}
              onClick={() => setExpandedSection('admin')}
              title="Administrasjon"
              count={10}
            />
          </div>

          {expandedSection === 'auth' && <AuthApiSection />}
          {expandedSection === 'study-plans' && <StudyPlansApiSection />}
          {expandedSection === 'literature' && <LiteratureApiSection />}
          {expandedSection === 'exams' && <ExamsApiSection />}
          {expandedSection === 'admin' && <AdminApiSection />}

          <h3 className="mt-8">Feilhåndtering</h3>
          <ul>
            <li><code>200</code> - Suksess</li>
            <li><code>201</code> - Opprettet</li>
            <li><code>400</code> - Ugyldig forespørsel</li>
            <li><code>401</code> - Ikke autentisert</li>
            <li><code>403</code> - Ingen tilgang (mangler rolle)</li>
            <li><code>404</code> - Ikke funnet</li>
            <li><code>429</code> - For mange forespørsler</li>
            <li><code>500</code> - Serverfeil</li>
          </ul>
        </>
      )}
    </div>
  );
}

function ApiSectionButton({ active, onClick, title, count }: {
  active: boolean;
  onClick: () => void;
  title: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-lg border transition ${
        active
          ? 'bg-kf-primary text-white border-kf-primary'
          : 'bg-white text-gray-700 border-gray-200 hover:border-kf-primary'
      }`}
    >
      <div className="flex justify-between items-center">
        <span className="font-medium">{title}</span>
        <span className={`text-sm ${active ? 'text-white/80' : 'text-gray-500'}`}>
          {count} endpoints
        </span>
      </div>
    </button>
  );
}

function AuthApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/login</h3>
        <p className="text-sm text-gray-600 mb-4">Logger inn en bruker og returnerer JWT token.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com",
  "password": "sikkerPassord123",
  "device_fingerprint": "unique-device-id"
}

// Response
{
  "success": true,
  "token": "jwt-token-here",
  "user": {
    "id": "uuid",
    "email": "bruker@example.com",
    "role": "member",
    "study_association_id": "uuid"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/register</h3>
        <p className="text-sm text-gray-600 mb-4">Registrerer en ny bruker (krever e-postverifisering).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "ny@example.com",
  "password": "sikkerPassord123",
  "full_name": "Fornavn Etternavn",
  "study_association_id": "uuid",
  "role": "member"
}

// Response
{
  "success": true,
  "message": "Registrering vellykket. Sjekk e-post for verifiseringskode.",
  "user_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/verify-email</h3>
        <p className="text-sm text-gray-600 mb-4">Verifiserer e-postadresse med kode.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com",
  "code": "123456"
}

// Response
{
  "success": true,
  "message": "E-post verifisert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/forgot-password</h3>
        <p className="text-sm text-gray-600 mb-4">Sender tilbakestillingslenke på e-post.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "email": "bruker@example.com"
}

// Response
{
  "success": true,
  "message": "Tilbakestillingslenke sendt på e-post"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /auth/reset-password</h3>
        <p className="text-sm text-gray-600 mb-4">Tilbakestiller passord med token fra e-post.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "token": "reset-token-from-email",
  "new_password": "nyttSikkertPassord123"
}

// Response
{
  "success": true,
  "message": "Passord oppdatert"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function StudyPlansApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/list</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over studieplaner. RLS filtrerer basert på brukerrolle.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "status": "approved", // Valgfritt: draft, submitted, under_review, approved, rejected
  "limit": 50,
  "offset": 0
}

// Response
{
  "success": true,
  "study_plans": [
    {
      "id": "uuid",
      "title": "Studieplan tittel",
      "ects_credits": 5,
      "status": "approved",
      "created_at": "2025-11-12T10:00:00Z"
    }
  ],
  "total": 10
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/get</h3>
        <p className="text-sm text-gray-600 mb-4">Henter en enkelt studieplan med full detalj.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid"
}

// Response
{
  "success": true,
  "study_plan": {
    "id": "uuid",
    "title": "Studieplan tittel",
    "description": "Beskrivelse",
    "ects_credits": 5,
    "estimated_hours": 125,
    "learning_outcomes": {
      "knowledge": ["Kunnskap 1", "Kunnskap 2"],
      "skills": ["Ferdighet 1"],
      "competence": ["Kompetanse 1"]
    },
    "content_modules": [
      {
        "title": "Modul 1",
        "description": "Beskrivelse",
        "hours": 40
      }
    ],
    "assessment_criteria": "Vurderingskriterier",
    "validation_score": 85,
    "status": "approved",
    "approval_link": "unique-link"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/create</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter en ny studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Ny studieplan",
  "description": "Beskrivelse",
  "ects_credits": 5,
  "learning_outcomes": {
    "knowledge": ["Kunnskap 1"],
    "skills": ["Ferdighet 1"],
    "competence": ["Kompetanse 1"]
  },
  "content_modules": [...],
  "assessment_criteria": "Kriterier",
  "created_by": "uuid",
  "study_association_id": "uuid"
}

// Response
{
  "success": true,
  "study_plan_id": "uuid",
  "message": "Studieplan opprettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/update</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer eksisterende studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid",
  "title": "Oppdatert tittel",
  "description": "Oppdatert beskrivelse",
  // ... andre felt ...
}

// Response
{
  "success": true,
  "message": "Studieplan oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/validate</h3>
        <p className="text-sm text-gray-600 mb-4">Validerer studieplan med AI (OpenAI GPT-4).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "user_id": "uuid"
}

// Response
{
  "success": true,
  "validation": {
    "overall_score": 85,
    "summary": "Oppsummering av validering",
    "learning_outcomes_quality": {
      "score": 90,
      "feedback": "Tilbakemelding"
    },
    "ects_alignment": { ... },
    "improvement_suggestions": [...]
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/approve</h3>
        <p className="text-sm text-gray-600 mb-4">Godkjenner en studieplan (admin/systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "admin_id": "uuid"
}

// Response
{
  "success": true,
  "approval_link": "https://.../verify/unique-link",
  "qr_code_url": "https://.../qr/..."
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/reject</h3>
        <p className="text-sm text-gray-600 mb-4">Avviser en studieplan med grunn.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "admin_id": "uuid",
  "rejection_reason": "Mangler læringsutbytte"
}

// Response
{
  "success": true,
  "message": "Studieplan avvist"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function LiteratureApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold text-blue-800">Hybrid AI Litteratursystem (v1.6)</h4>
        <p className="text-sm text-blue-700">
          Systemet kombinerer AI-genererte forslag med kurerte kilder for å redusere hallusinasjoner.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/study-plans/:id/literature</h3>
        <p className="text-sm text-gray-600 mb-4">Henter alle litteraturkilder for en studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "literature": [
    {
      "id": "uuid",
      "title": "Regjeringen - Nasjonal strategi",
      "url": "https://regjeringen.no/...",
      "literature_type": "webpage",
      "from_curated": true,
      "quality_level": 5
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature</h3>
        <p className="text-sm text-gray-600 mb-4">Legger til manuell litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Bok tittel",
  "url": "https://example.com/bok.pdf",
  "description": "Beskrivelse av kilden",
  "literature_type": "pdf" // pdf, webpage, word, other
}

// Response
{
  "success": true,
  "literature_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature/ai-suggest</h3>
        <p className="text-sm text-gray-600 mb-4">Genererer KI-forslag (hybrid med kurerte kilder).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "suggestions": [
    {
      "title": "NDLA - Samfunnsfag",
      "url": "https://ndla.no/...",
      "description": "Beskrivelse",
      "literature_type": "webpage",
      "relevance_score": 95,
      "from_curated": true,
      "curated_source_id": "uuid"
    },
    {
      "title": "AI-generert kilde",
      "url": "https://...",
      "from_curated": false
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">PUT /api/study-plans/:id/literature/:lit_id</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Oppdatert tittel",
  "description": "Oppdatert beskrivelse"
}

// Response
{
  "success": true,
  "message": "Litteratur oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">DELETE /api/study-plans/:id/literature/:lit_id</h3>
        <p className="text-sm text-gray-600 mb-4">Sletter litteraturkilde (soft delete).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "message": "Litteratur slettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/study-plans/:id/literature/:lit_id/download</h3>
        <p className="text-sm text-gray-600 mb-4">Laster ned og prosesserer litteraturfil (max 50MB).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "download_status": "downloaded",
  "local_file_path": "/path/to/file.pdf",
  "file_size": 1048576
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function ExamsApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/exams/list</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over prøvesett.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "exam_sets": [
    {
      "id": "uuid",
      "title": "Eksamen tittel",
      "description": "Beskrivelse",
      "question_count": 20,
      "created_at": "2025-11-12T10:00:00Z"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exams/generate</h3>
        <p className="text-sm text-gray-600 mb-4">Genererer prøve med AI fra studieplan.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "study_plan_id": "uuid",
  "question_count": 20,
  "include_literature": true
}

// Response
{
  "success": true,
  "exam_set_id": "uuid",
  "questions": [
    {
      "id": "uuid",
      "question_text": "Spørsmål her?",
      "question_type": "multiple_choice",
      "options": [
        {"id": "a", "text": "Alternativ A"},
        {"id": "b", "text": "Alternativ B"}
      ],
      "correct_answer": "a"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/v1/exam/start</h3>
        <p className="text-sm text-gray-600 mb-4">Starter en ny prøvesesjon for kandidat.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "exam_set_id": "uuid",
  "candidate_id": "unique-lms-id",
  "anonymous_id": "student-123"
}

// Response
{
  "success": true,
  "session_id": "uuid",
  "exam_url": "https://.../exam/session-id",
  "expires_at": "2025-11-12T14:00:00Z"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exam/save-answer</h3>
        <p className="text-sm text-gray-600 mb-4">Lagrer svar på prøvespørsmål (auto-save).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "session_id": "uuid",
  "anonymous_id": "student-123",
  "question_id": "uuid",
  "answer_text": "Svar tekst", // For open-ended
  "selected_option": "a" // For multiple choice
}

// Response
{
  "success": true,
  "message": "Svar lagret"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/exam/submit</h3>
        <p className="text-sm text-gray-600 mb-4">Leverer inn prøve for sensur.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "session_id": "uuid",
  "anonymous_id": "student-123"
}

// Response
{
  "success": true,
  "message": "Eksamen levert",
  "status": "submitted"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/v1/exam/result/:session_id</h3>
        <p className="text-sm text-gray-600 mb-4">Henter prøveresultat.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "result": {
    "session_id": "uuid",
    "candidate_id": "unique-id",
    "status": "completed",
    "total_questions": 20,
    "correct_answers": 17,
    "score": 85,
    "passed": true,
    "graded_at": "2025-11-12T14:30:00Z"
  }
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/accommodations/templates</h3>
        <p className="text-sm text-gray-600 mb-4">Henter tilgjengelige tilretteleggingsmaler.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "templates": [
    {
      "id": "uuid",
      "name": "Dysleksi - 50% ekstra tid",
      "accommodation_type": "extra_time",
      "default_time_multiplier": 1.5
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/accommodations/session/:session_id</h3>
        <p className="text-sm text-gray-600 mb-4">Legger til tilrettelegging for prøvesesjon.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "template_id": "uuid",
  "description": "Ekstra beskrivelse",
  "time_multiplier": 1.5
}

// Response
{
  "success": true,
  "accommodation_id": "uuid",
  "new_expires_at": "2025-11-12T15:00:00Z"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function AdminApiSection() {
  return (
    <div className="space-y-8 mt-8">
      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
        <h4 className="font-semibold text-yellow-800">Administratorrettigheter påkrevd</h4>
        <p className="text-sm text-yellow-700">
          Alle endpoints i denne kategorien krever admin eller systemadmin rolle.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/users</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over brukere (filtrert av RLS).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "users": [
    {
      "id": "uuid",
      "email": "user@example.com",
      "full_name": "Fornavn Etternavn",
      "role": "member",
      "status": "active",
      "study_association_id": "uuid"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/users/approve</h3>
        <p className="text-sm text-gray-600 mb-4">Godkjenner en ventende bruker.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "admin_id": "uuid"
}

// Response
{
  "success": true,
  "message": "Bruker godkjent"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/users/update</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer brukerdata.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "user_id": "uuid",
  "full_name": "Nytt navn",
  "role": "admin" // Kun systemadmin kan endre roller
}

// Response
{
  "success": true,
  "message": "Bruker oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/study-associations</h3>
        <p className="text-sm text-gray-600 mb-4">Henter studieforbund (systemadmin får alle, admin får eget).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "study_associations": [
    {
      "id": "uuid",
      "name": "Kompetanseforbundet",
      "organization_number": "123456789",
      "active": true
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/backups</h3>
        <p className="text-sm text-gray-600 mb-4">Henter liste over database-sikkerhetskopier (systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "backups": [
    {
      "id": "uuid",
      "filename": "backup_20251112.sql",
      "size": 15728640,
      "created_at": "2025-11-12T02:00:00Z"
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/backups/create</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter ny database-sikkerhetskopi.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "backup_id": "uuid",
  "filename": "backup_20251112_143000.sql",
  "message": "Sikkerhetskopi opprettet"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">GET /api/admin/curated-sources</h3>
        <p className="text-sm text-gray-600 mb-4">Henter kurerte litteraturkilder (v1.6 - systemadmin).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "sources": [
    {
      "id": "uuid",
      "title": "Regjeringen.no - Nasjonal strategi",
      "url": "https://regjeringen.no/...",
      "description": "Offisiell strategi",
      "literature_type": "webpage",
      "categories": ["samfunnsfag", "politikk"],
      "quality_level": 5,
      "usage_count": 15,
      "active": true
    }
  ]
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">POST /api/admin/curated-sources</h3>
        <p className="text-sm text-gray-600 mb-4">Oppretter ny kurert litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "NDLA - Samfunnsfag",
  "url": "https://ndla.no/...",
  "description": "Gratis læringsressurser",
  "literature_type": "webpage",
  "categories": ["samfunnsfag", "pedagogikk"],
  "quality_level": 5
}

// Response
{
  "success": true,
  "source_id": "uuid"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">PUT /api/admin/curated-sources/:id</h3>
        <p className="text-sm text-gray-600 mb-4">Oppdaterer kurert litteraturkilde.</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Request
{
  "title": "Oppdatert tittel",
  "categories": ["ny-kategori"],
  "quality_level": 4
}

// Response
{
  "success": true,
  "message": "Kilde oppdatert"
}`}</pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-kf-primary">DELETE /api/admin/curated-sources/:id</h3>
        <p className="text-sm text-gray-600 mb-4">Sletter kurert litteraturkilde (hard delete).</p>
        <div className="bg-gray-900 text-gray-100 p-4 rounded-lg">
          <pre className="text-sm overflow-x-auto">{`// Response
{
  "success": true,
  "message": "Kilde slettet"
}`}</pre>
        </div>
      </div>
    </div>
  );
}

function DatabaseSection() {
  return (
    <div className="prose max-w-none">
      <h1>Database-skjema</h1>

      <h2>Hovedtabeller</h2>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">study_associations</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Studieforbund (organisasjoner)</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">name</td><td>VARCHAR(255)</td><td>Studieforbundets navn</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">org_number</td><td>VARCHAR(20)</td><td>Organisasjonsnummer</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">active</td><td>BOOLEAN</td><td>Om studieforbundet er aktivt</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">created_at</td><td>TIMESTAMPTZ</td><td>Opprettet tidspunkt</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">members</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Medlemsorganisasjoner</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">study_association_id</td><td>UUID</td><td>FK til study_associations</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">organization_name</td><td>VARCHAR(255)</td><td>Organisasjonsnavn</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">status</td><td>VARCHAR(20)</td><td>active / suspended / inactive</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">ai_requests_count</td><td>INTEGER</td><td>Antall AI-forespørsler (fakturering)</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="my-6">
        <h3 className="bg-kf-primary text-white px-4 py-2 rounded-t">users</h3>
        <div className="bg-gray-50 p-4 rounded-b border border-gray-200">
          <p className="text-sm text-gray-600 mb-3">Systembrukere</p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Kolonne</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Beskrivelse</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b"><td className="py-2 font-mono">id</td><td>UUID</td><td>Primary key (synkronisert med auth.users)</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">email</td><td>VARCHAR(255)</td><td>E-postadresse (unik)</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">role</td><td>user_role</td><td>systemadmin / admin / member / sensor</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">status</td><td>VARCHAR(50)</td><td>pending_approval / active / rejected</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">study_association_id</td><td>UUID</td><td>FK til study_associations</td></tr>
              <tr className="border-b"><td className="py-2 font-mono">member_id</td><td>UUID</td><td>FK til members (for member/sensor)</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Row Level Security (RLS)</h2>
      <p>Alle tabeller har RLS aktivert for å sikre tilgangskontroll på database-nivå:</p>
      <ul>
        <li><strong>systemadmin:</strong> Full tilgang til alle data</li>
        <li><strong>admin:</strong> Tilgang kun til data for eget studieforbund</li>
        <li><strong>member:</strong> Tilgang til egne data og sin medlemsorganisasjon</li>
        <li><strong>sensor:</strong> Tilgang til prøver de er tildelt</li>
      </ul>
    </div>
  );
}

function CodeExamplesSection() {
  return (
    <div className="prose max-w-none">
      <h1>Kodeeksempler</h1>

      <h2>Python</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`import requests
import json

# API credentials
API_USERNAME = "your-api-username"
API_KEY = "your-secret-key"
BASE_URL = "https://studieplan.kompetanseattest.no/api"

# Headers
headers = {
    "X-API-Username": API_USERNAME,
    "X-API-Key": API_KEY,
    "Content-Type": "application/json"
}

# Start exam session
def start_exam(exam_set_id, candidate_id):
    url = f"{BASE_URL}/v1/exam/start"
    payload = {
        "exam_set_id": exam_set_id,
        "candidate_id": candidate_id,
        "anonymous_id": f"student-{candidate_id}"
    }

    response = requests.post(url, headers=headers, json=payload)

    if response.status_code == 200:
        data = response.json()
        if data["success"]:
            print(f"Exam started: {data['exam_url']}")
            return data["session_id"]
    else:
        print(f"Error: {response.status_code}")
        return None

# Get exam result
def get_result(session_id):
    url = f"{BASE_URL}/v1/exam/result/{session_id}"
    response = requests.get(url, headers=headers)

    if response.status_code == 200:
        data = response.json()
        if data["success"]:
            result = data["result"]
            print(f"Score: {result['score']}%")
            print(f"Passed: {result['passed']}")
            return result
    return None

# Example usage
session_id = start_exam("exam-uuid", "12345")
if session_id:
    result = get_result(session_id)`}
        </pre>
      </div>

      <h2>JavaScript (Node.js)</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`const axios = require('axios');

const API_USERNAME = 'your-api-username';
const API_KEY = 'your-secret-key';
const BASE_URL = 'https://studieplan.kompetanseattest.no/api';

// Configure axios
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'X-API-Username': API_USERNAME,
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json'
  }
});

// Start exam
async function startExam(examSetId, candidateId) {
  try {
    const response = await api.post('/v1/exam/start', {
      exam_set_id: examSetId,
      candidate_id: candidateId,
      anonymous_id: \`student-\${candidateId}\`
    });

    if (response.data.success) {
      console.log('Exam URL:', response.data.exam_url);
      return response.data.session_id;
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    return null;
  }
}

// Get result
async function getResult(sessionId) {
  try {
    const response = await api.get(\`/v1/exam/result/\${sessionId}\`);

    if (response.data.success) {
      const result = response.data.result;
      console.log(\`Score: \${result.score}%\`);
      console.log(\`Passed: \${result.passed}\`);
      return result;
    }
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    return null;
  }
}

// Example usage
(async () => {
  const sessionId = await startExam('exam-uuid', '12345');
  if (sessionId) {
    const result = await getResult(sessionId);
  }
})();`}
        </pre>
      </div>

      <h2>PHP</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`<?php

class KompetanseatestAPI {
    private $baseUrl = 'https://studieplan.kompetanseattest.no/api';
    private $username;
    private $apiKey;

    public function __construct($username, $apiKey) {
        $this->username = $username;
        $this->apiKey = $apiKey;
    }

    private function makeRequest($method, $endpoint, $data = null) {
        $ch = curl_init($this->baseUrl . $endpoint);

        $headers = [
            'X-API-Username: ' . $this->username,
            'X-API-Key: ' . $this->apiKey,
            'Content-Type: application/json'
        ];

        curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
        curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

        if ($method === 'POST') {
            curl_setopt($ch, CURLOPT_POST, true);
            curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
        }

        $response = curl_exec($ch);
        $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        curl_close($ch);

        if ($httpCode === 200) {
            return json_decode($response, true);
        }

        return null;
    }

    public function startExam($examSetId, $candidateId) {
        $data = [
            'exam_set_id' => $examSetId,
            'candidate_id' => $candidateId,
            'anonymous_id' => 'student-' . $candidateId
        ];

        $result = $this->makeRequest('POST', '/v1/exam/start', $data);

        if ($result && $result['success']) {
            echo "Exam URL: " . $result['exam_url'] . "\\n";
            return $result['session_id'];
        }

        return null;
    }

    public function getResult($sessionId) {
        $result = $this->makeRequest('GET', '/v1/exam/result/' . $sessionId);

        if ($result && $result['success']) {
            $examResult = $result['result'];
            echo "Score: " . $examResult['score'] . "%\\n";
            echo "Passed: " . ($examResult['passed'] ? 'Yes' : 'No') . "\\n";
            return $examResult;
        }

        return null;
    }
}

// Example usage
$api = new KompetanseatestAPI('your-api-username', 'your-secret-key');
$sessionId = $api->startExam('exam-uuid', '12345');

if ($sessionId) {
    $result = $api->getResult($sessionId);
}

?>`}
        </pre>
      </div>
    </div>
  );
}

function DeploymentSection() {
  return (
    <div className="prose max-w-none">
      <h1>Deployment Guide</h1>

      <div className="bg-red-50 border-l-4 border-red-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Viktig!</h3>
            <p className="mt-2 text-sm text-red-700">
              Deploy alltid til test-miljøet først. Test grundig før deployment til produksjon.
            </p>
          </div>
        </div>
      </div>

      <h2>Deployment-prosess</h2>
      <ol>
        <li><strong>Forberedelser</strong>
          <ul>
            <li>Sjekk at alle tester kjører</li>
            <li>Verifiser at migrations er oppdatert</li>
            <li>Oppdater CHANGELOG.md</li>
          </ul>
        </li>
        <li><strong>Test Environment</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`cd /root/studieplan
npm run build
rsync -av --delete /root/studieplan/dist/ /root/studieplan-deploy/test/frontend/
docker restart kvalitetssikring-email-test`}
            </pre>
          </div>
        </li>
        <li><strong>Database Migrations</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`docker exec -i supabase-db psql -U postgres -d kvalitetssikring_test \\
  < /root/studieplan/supabase/migrations/[migration-file].sql`}
            </pre>
          </div>
        </li>
        <li><strong>Testing</strong>
          <ul>
            <li>Test alle kritiske flyter</li>
            <li>Verifiser at nye funksjoner fungerer</li>
            <li>Sjekk logger for feil</li>
          </ul>
        </li>
        <li><strong>Production Deploy</strong>
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-2">
            <pre className="text-sm overflow-x-auto">
{`rsync -av --delete /root/studieplan/dist/ /root/studieplan-deploy/prod/frontend/
docker restart kvalitetssikring-email-prod

# Apply migrations to prod
docker exec -i supabase-db psql -U postgres -d kvalitetssikring_prod \\
  < /root/studieplan/supabase/migrations/[migration-file].sql`}
            </pre>
          </div>
        </li>
      </ol>

      <h2>Rollback-prosedyre</h2>
      <p>Hvis deployment feiler eller medfører kritiske feil:</p>
      <ol>
        <li>Reverter frontend-kode til forrige versjon</li>
        <li>Restart Docker-containere</li>
        <li>Kjør rollback-migrations hvis nødvendig</li>
        <li>Varsle brukere om nedetid hvis relevant</li>
      </ol>

      <h2>Overvåkning etter deployment</h2>
      <ul>
        <li>Overvåk feillogger i 24 timer</li>
        <li>Sjekk systemytelse og responstider</li>
        <li>Verifiser at alle integrasjoner fungerer</li>
        <li>Følg opp brukerrapporterte problemer</li>
      </ul>
    </div>
  );
}

function SecuritySection() {
  return (
    <div className="prose max-w-none">
      <h1>Sikkerhet og Best Practices</h1>

      <h2>Autentisering og Autorisasjon</h2>
      <ul>
        <li><strong>Row Level Security (RLS):</strong> Alle database-tabeller har RLS aktivert</li>
        <li><strong>JWT Tokens:</strong> 7-dagers utløpstid</li>
        <li><strong>Password Hashing:</strong> bcrypt med høy cost factor</li>
        <li><strong>2FA:</strong> Støtte for tofaktorautentisering</li>
      </ul>

      <h2>API-sikkerhet</h2>
      <ul>
        <li><strong>Rate Limiting:</strong> 1000 requests/time per API-nøkkel</li>
        <li><strong>IP Whitelisting:</strong> Valgfritt per API-nøkkel</li>
        <li><strong>HTTPS-only:</strong> All kommunikasjon over TLS</li>
        <li><strong>API Key Rotation:</strong> Mulighet for å regenerere nøkler</li>
      </ul>

      <h2>Data-sikkerhet</h2>
      <ul>
        <li><strong>Kryptering i transit:</strong> TLS 1.3</li>
        <li><strong>Kryptering at rest:</strong> PostgreSQL encryption</li>
        <li><strong>Backup:</strong> Daglige automatiske backups</li>
        <li><strong>Audit Logging:</strong> Alle kritiske operasjoner logges</li>
      </ul>

      <h2>GDPR Compliance</h2>
      <ul>
        <li>Minimal datainnsamling</li>
        <li>Brukersamtykke for e-post</li>
        <li>Rett til sletting (soft delete)</li>
        <li>Data portability (eksport til PDF/CSV)</li>
        <li>Audit trail for alle dataoperasjoner</li>
      </ul>

      <h2>Best Practices</h2>
      <div className="bg-green-50 p-4 rounded-lg my-4">
        <h3 className="text-green-800">✓ Gjør dette</h3>
        <ul className="text-sm">
          <li>Bruk miljøvariabler for hemmeligheter</li>
          <li>Roter API-nøkler regelmessig</li>
          <li>Overvåk login_attempts for mistenkelig aktivitet</li>
          <li>Hold alle dependencies oppdatert</li>
          <li>Test backups regelmessig</li>
        </ul>
      </div>

      <div className="bg-red-50 p-4 rounded-lg my-4">
        <h3 className="text-red-800">✗ Ikke gjør dette</h3>
        <ul className="text-sm">
          <li>Commit hemmeligheter til Git</li>
          <li>Bruk samme passord på tvers av miljøer</li>
          <li>Eksponer database direkte til internett</li>
          <li>Kjør som root i Docker-containere</li>
          <li>Deaktiver HTTPS i produksjon</li>
        </ul>
      </div>
    </div>
  );
}

function TroubleshootingSection() {
  return (
    <div className="prose max-w-none">
      <h1>Feilsøking</h1>

      <h2>Vanlige problemer</h2>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Kan ikke logge inn</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker og løsninger:</p>
        <ol className="text-sm">
          <li><strong>E-post ikke bekreftet:</strong> Sjekk auth.users.confirmed_at</li>
          <li><strong>Medlemsorganisasjon suspendert:</strong> Sjekk members.status</li>
          <li><strong>RLS blokkerer:</strong> Sjekk at policies er korrekte</li>
          <li><strong>Ugyldig passord:</strong> Reset passord via admin-panel</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: API returnerer 403 Forbidden</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Medlemsorganisasjon inaktiv:</strong> Sjekk members.status</li>
          <li><strong>IP ikke whitelisted:</strong> Sjekk api_credentials.allowed_ips</li>
          <li><strong>API-nøkkel utløpt:</strong> Regenerer nøkkel</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Frontend lastes ikke</h3>
        <p className="text-sm text-gray-600 my-2">Feilsøkingstrinn:</p>
        <ol className="text-sm">
          <li>Sjekk at Docker-container kjører: <code>docker ps</code></li>
          <li>Sjekk Caddy-logs: <code>docker logs caddy-test</code></li>
          <li>Verifiser at frontend-filer er deployet korrekt</li>
          <li>Sjekk nettverksoppkobling fra Caddy til frontend</li>
        </ol>
      </div>

      <h2>Nyttige kommandoer</h2>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`# Sjekk status på alle containere
docker ps

# Vis logger for en service
docker logs -f kvalitetssikring-email-test

# Restart en service
docker restart kvalitetssikring-email-test

# Kjør SQL-query
docker exec -i supabase-db psql -U postgres -d kvalitetssikring_test \\
  -c "SELECT * FROM users WHERE email = 'test@example.com';"

# Sjekk database-connections
docker exec supabase-db psql -U postgres -c \\
  "SELECT count(*) FROM pg_stat_activity;"

# Backup database
docker exec supabase-db pg_dump -U postgres kvalitetssikring_test \\
  > backup_\`date +%Y%m%d\`.sql`}
        </pre>
      </div>

      <h2>Kontaktinformasjon</h2>
      <p>Ved kritiske feil eller spørsmål, kontakt:</p>
      <ul>
        <li><strong>Teknisk support:</strong> support@kompetanseforbundet.no</li>
        <li><strong>Systemadministrator:</strong> [admin@kompetanseforbundet.no]</li>
      </ul>
    </div>
  );
}

function XtramileSection() {
  return (
    <div className="prose max-w-none">
      <h1>Xtramile LMS-integrasjon</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <Link2 className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Automatisk sertifikatgenerering</h3>
            <p className="mt-2 text-sm text-blue-700">
              Xtramile-integrasjonen gjør det mulig å automatisk utstede kompetanseattester
              når deltakere fullfører kurs i Xtramile LMS.
            </p>
          </div>
        </div>
      </div>

      <h2>Hvordan det fungerer</h2>
      <ol>
        <li><strong>Koble til Xtramile:</strong> Organisasjonen legger inn sin API-nøkkel fra Xtramile</li>
        <li><strong>Kursmapping:</strong> Xtramile-kurs kobles til godkjente studieplaner i systemet</li>
        <li><strong>Automatisk synkronisering:</strong> Systemet henter fullføringer fra Xtramile regelmessig</li>
        <li><strong>Sertifikatutstedelse:</strong> Kompetanseattester utstedes automatisk til kandidater</li>
      </ol>

      <div className="bg-gray-50 p-6 rounded-lg my-6 font-mono text-sm">
        <pre className="whitespace-pre overflow-x-auto">
{`┌─────────────────┐         ┌─────────────────┐         ┌─────────────────┐
│   Xtramile      │         │   Vårt system   │         │  Kandidat-      │
│   (LMS)         │         │   (Backend)     │         │  portal         │
└────────┬────────┘         └────────┬────────┘         └────────┬────────┘
         │                           │                           │
         │  GET /statistics          │                           │
         │  ?lastActivityDate=X      │                           │
         │◄──────────────────────────┤ Cron jobb                 │
         │                           │ (hver 5 min)              │
         ├──────────────────────────►│                           │
         │  [Fullførte kurs]         │                           │
         │                           │                           │
         │                           ├──► Match e-post           │
         │                           │    med profil             │
         │                           │                           │
         │                           ├──► Finn mapping           │
         │                           │    kurs→studieplan        │
         │                           │                           │
         │                           ├──► Generer                │
         │                           │    kompetanseattest       │
         │                           │                           │
         │                           ├──────────────────────────►│
         │                           │    E-post: "Ny attest"    │
         │                           │                           │`}
        </pre>
      </div>

      <h2>Oppsett steg for steg</h2>

      <h3>1. Få API-nøkkel fra Xtramile</h3>
      <p>
        Kontakt Xtramile-administrasjonen for å få utstedt en API-nøkkel for din organisasjon.
        API-nøkkelen gir tilgang til å hente kurs- og fullføringsdata.
      </p>

      <h3>2. Aktiver integrasjonen</h3>
      <ol>
        <li>Gå til <strong>Innstillinger → Xtramile LMS</strong></li>
        <li>Godta databehandlingsavtalen</li>
        <li>Lim inn API-nøkkelen</li>
        <li>Klikk "Aktiver integrasjon"</li>
      </ol>

      <h3>3. Test tilkoblingen</h3>
      <p>
        Klikk "Test tilkobling" for å verifisere at API-nøkkelen fungerer.
        Ved vellykket tilkobling vises antall kurs funnet i Xtramile.
      </p>

      <h3>4. Opprett kursmappinger</h3>
      <p>
        For hvert Xtramile-kurs som skal utstede kompetanseattester:
      </p>
      <ol>
        <li>Klikk "Legg til kursmapping"</li>
        <li>Fyll inn Xtramile kurs-ID (UUID)</li>
        <li>Velg hvilken godkjent studieplan kurset skal kobles til</li>
        <li>Lagre mappingen</li>
      </ol>

      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Viktig om kursmapping</h3>
            <p className="mt-2 text-sm text-yellow-700">
              Kun <strong>godkjente</strong> studieplaner kan kobles til Xtramile-kurs.
              Studieplanen må være fullstendig validert og godkjent av studieforbundet
              før kursmapping kan opprettes.
            </p>
          </div>
        </div>
      </div>

      <h2>Synkronisering</h2>
      <p>
        Systemet synkroniserer automatisk fullføringer fra Xtramile basert på konfigurert intervall
        (standard: hvert 5. minutt).
      </p>

      <h3>Automatisk sync</h3>
      <ul>
        <li>Kjører i bakgrunnen</li>
        <li>Henter kun nye fullføringer siden forrige synkronisering</li>
        <li>Respekterer Xtramile API rate limits</li>
      </ul>

      <h3>Manuell sync</h3>
      <p>
        Du kan også trigge manuell synkronisering ved å klikke "Synkroniser nå" i Xtramile-innstillingene.
        Dette er nyttig ved feilsøking eller for å hente data umiddelbart.
      </p>

      <h2>Kandidat-matching</h2>
      <p>
        Systemet matcher kandidater basert på e-postadresse:
      </p>
      <ul>
        <li>
          <strong>Eksisterende kandidat:</strong> Hvis e-postadressen allerede finnes i Kandidatportalen,
          utstedes kompetanseattest direkte til denne profilen.
        </li>
        <li>
          <strong>Ny kandidat:</strong> Hvis e-postadressen ikke finnes, opprettes en midlertidig profil
          og kandidaten mottar invitasjon til å registrere seg i Kandidatportalen.
        </li>
      </ul>

      <h2>Synkroniseringslogg</h2>
      <p>
        Alle synkroniseringer logges med følgende informasjon:
      </p>
      <table>
        <thead>
          <tr>
            <th>Felt</th>
            <th>Beskrivelse</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Tidspunkt</td>
            <td>Når synkroniseringen ble utført</td>
          </tr>
          <tr>
            <td>E-post</td>
            <td>Kandidatens e-postadresse</td>
          </tr>
          <tr>
            <td>Kurs</td>
            <td>Xtramile-kurset som ble fullført</td>
          </tr>
          <tr>
            <td>Status</td>
            <td>Om kompetanseattest ble generert eller feilet</td>
          </tr>
        </tbody>
      </table>

      <h2>Feilsøking</h2>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Tilkobling feiler</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Feil API-nøkkel:</strong> Verifiser at nøkkelen er korrekt kopiert</li>
          <li><strong>Nøkkel utgått:</strong> Kontakt Xtramile for ny nøkkel</li>
          <li><strong>Nettverksproblem:</strong> Sjekk at serveren har tilgang til api.xtramile.no</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Ingen attester genereres</h3>
        <p className="text-sm text-gray-600 my-2">Sjekk følgende:</p>
        <ol className="text-sm">
          <li><strong>Kursmapping mangler:</strong> Verifiser at Xtramile-kurset er mappet til en studieplan</li>
          <li><strong>Studieplan ikke godkjent:</strong> Kun godkjente studieplaner kan utstede attester</li>
          <li><strong>Deltaker ikke bestått:</strong> Kun fullførte og beståtte kurs genererer attester</li>
          <li><strong>Allerede utstedt:</strong> Systemet sjekker for duplikater</li>
        </ol>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 my-4">
        <h3 className="text-lg font-semibold text-gray-900">Problem: Kandidat finner ikke attest</h3>
        <p className="text-sm text-gray-600 my-2">Mulige årsaker:</p>
        <ol className="text-sm">
          <li><strong>Annen e-post:</strong> E-posten i Xtramile må matche Kandidatportal-profilen</li>
          <li><strong>Ikke registrert:</strong> Kandidaten må registrere seg i Kandidatportalen</li>
          <li><strong>Synkronisering pågår:</strong> Vent på neste sync-intervall</li>
        </ol>
      </div>

      <h2>API-referanse</h2>
      <p>
        Xtramile-integrasjonen bruker følgende Xtramile API-endepunkter:
      </p>

      <table>
        <thead>
          <tr>
            <th>Endpoint</th>
            <th>Beskrivelse</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>GET /api/v1/statistics</code></td>
            <td>Henter fullføringsdata (hovedkilde)</td>
          </tr>
          <tr>
            <td><code>GET /api/v1/courses</code></td>
            <td>Henter kursinformasjon for mapping</td>
          </tr>
        </tbody>
      </table>

      <h3>Fullføringsdata fra Xtramile</h3>
      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg my-4">
        <pre className="text-sm overflow-x-auto">
{`{
  "courseId": "uuid",
  "courseName": "HMS-kurs for ledere",
  "person": "Ola Nordmann",
  "email": "ola@example.com",
  "completedDate": "2026-03-08T14:30:00Z",
  "isPassed": true,
  "quizScore": 85,
  "quizScoreTotal": 100,
  "certificationId": "uuid"
}`}
        </pre>
      </div>

      <h2>Sikkerhet</h2>
      <ul>
        <li><strong>API-nøkkel:</strong> Lagres kryptert (AES-256-GCM) i databasen</li>
        <li><strong>Dataminimering:</strong> Kun nødvendig data overføres</li>
        <li><strong>Logging:</strong> Alle synkroniseringer logges for revisjon</li>
        <li><strong>Tilgangskontroll:</strong> Kun organisasjonsadministratorer kan konfigurere</li>
      </ul>

      <h2>Støtte</h2>
      <p>
        Ved spørsmål om Xtramile-integrasjonen, kontakt:
      </p>
      <ul>
        <li><strong>Teknisk support:</strong> support@kompetanseforbundet.no</li>
        <li><strong>Xtramile-support:</strong> support@xtramile.no</li>
      </ul>
    </div>
  );
}
